import Phaser from 'phaser';
import { SCENES, DESIGN_WIDTH, DESIGN_HEIGHT, COLORS } from '../constants';
import { SceneRouter } from '../systems/SceneRouter';
import { InputRouter, PlayerId, ALL_PLAYER_IDS } from '../systems/InputRouter';
import { RenderSystem, RENDER_LAYERS } from '../systems/RenderSystem';
import { LevelLoader, LoadedLevel, BiomeType } from '../systems/LevelLoader';
import { CustomLevelManager } from '../systems/CustomLevelManager';
import { PathingSystem } from '../systems/PathingSystem';
import { TowerPlacementSystem } from '../systems/TowerPlacementSystem';
import { TowerUnlockSystem } from '../systems/TowerUnlockSystem';
import { WaveSpawner } from '../systems/WaveSpawner';
import { SessionManager } from '../systems/SessionManager';
import { DebugSystem } from '../systems/DebugSystem';
import { DropSystem } from '../systems/DropSystem';
import { TowerInteractionSystem } from '../systems/TowerInteractionSystem';
import { DanceSystem } from '../systems/DanceSystem';
import { VFXSystem } from '../systems/VFXSystem';
import { LevelStatsTracker } from '../systems/LevelStatsTracker';
import { AudioRouter } from '../systems/AudioRouter';
import { EntityManager, Player, Monster, Tower, Boss, Base, TreeSlot, TowerType } from '../entities';
import type { MonsterType, BossType } from '../entities';
import { GameState } from '../state/GameState';
import { ProgressState } from '../state/ProgressState';
import { SettingsState } from '../state/SettingsState';
import { UIButton } from '../ui/UIButton';
import { UISlider } from '../ui/UISlider';
import { UIPanel } from '../ui/UIPanel';
import { BossHealthBar } from '../ui/BossHealthBar';
import { WavePreviewUI } from '../ui/WavePreviewUI';

// ============================================
// GameplayScene - Full Implementation
// ============================================

interface GameplaySceneData {
  scenarioId: string;
}

export class GameplayScene extends Phaser.Scene {
  private scenarioId: string = 'F1';
  private level: LoadedLevel | null = null;

  // Game state
  private isPaused: boolean = false;
  private isGameOver: boolean = false;

  // Per-player tower selection (supports up to 4 players)
  private playerTowerSelections: Map<PlayerId, TowerType> = new Map([
    ['P1', 'arrow'],
    ['P2', 'arrow'],
    ['P3', 'arrow'],
    ['P4', 'arrow'],
  ]);
  private availableTowers: TowerType[] = [];
  private towerSelectorBgs: Phaser.GameObjects.Rectangle[] = [];
  private towerSelectorTexts: Phaser.GameObjects.Text[] = [];
  private towerSelectorTicks: Map<string, Phaser.GameObjects.Polygon[]> = new Map(); // tower type -> triangle indicators

  // UI Elements
  private waveText!: Phaser.GameObjects.Text;
  private gemsText!: Phaser.GameObjects.Text;
  private coinsText!: Phaser.GameObjects.Text;
  private villagersText!: Phaser.GameObjects.Text;
  private monstersText!: Phaser.GameObjects.Text;
  private regionText!: Phaser.GameObjects.Text;

  // Pause menu
  private pauseOverlay: Phaser.GameObjects.Rectangle | null = null;
  private pausePanel: UIPanel | null = null;
  private pauseButtons: UIButton[] = [];
  private pauseSliders: UISlider[] = [];
  private pauseElements: (UIButton | UISlider)[] = [];
  private pauseSelectedIndex: number = 0;
  private pauseControlsVisible: boolean = false;
  private pauseControlsPanel: UIPanel | null = null;
  private pauseControlsBackButton: UIButton | null = null;

  // Boss UI
  private bossHealthBar: BossHealthBar | null = null;
  
  // Background music
  private bgMusic: Phaser.Sound.BaseSound | null = null;
  private musicTracks: string[] = ['bgm-1', 'bgm-2', 'bgm-3', 'bgm-4', 'bgm-5', 'bgm-6', 'bgm-7'];
  private currentTrackIndex: number = 0;
  private musicMuted: boolean = false;
  private musicButtonBg: Phaser.GameObjects.Rectangle | null = null;
  private musicButtonText: Phaser.GameObjects.Text | null = null;

  // Wave Preview UI
  private wavePreviewUI: WavePreviewUI | null = null;

  // Tree slot references for drops
  private treeSlots: Map<string, TreeSlot> = new Map();

  // Ground tile sprite (for experiment biome switching)
  private groundTileSprite: Phaser.GameObjects.TileSprite | null = null;
  // Path graphics (redrawn when experiment biome changes)
  private pathGraphics: Phaser.GameObjects.Graphics | null = null;
  // Path image masked by blurred path shape (BitmapMask from RenderTexture); used for forest/bog/ruins when path texture exists
  private pathMaskImage: Phaser.GameObjects.TileSprite | null = null;
  private pathMaskTexture: Phaser.GameObjects.RenderTexture | null = null;

  // Volcano-only: subtle floating embers (tiny black/glowing specs)
  private emberContainer: Phaser.GameObjects.Container | null = null;
  private emberTweens: Phaser.Tweens.Tween[] = [];

  // Visibility/focus handlers for auto-pause when window loses focus
  private boundVisibilityHandler: (() => void) | null = null;
  private boundBlurHandler: (() => void) | null = null;

  // Experiment mode
  private isExperimentMode: boolean = false;
  private spawnPanelContainer: Phaser.GameObjects.Container | null = null;

  // Victory countdown (allows players to grab coins/sell towers before results)
  private victoryCountdownActive: boolean = false;
  private victoryCountdownTime: number = 10; // seconds
  private victoryCountdownContainer: Phaser.GameObjects.Container | null = null;
  private victoryCountdownText: Phaser.GameObjects.Text | null = null;
  private experimentUIElements: Phaser.GameObjects.GameObject[] = [];

  // Tutorial mode (Tutorial scenario): step 0=intro, 1=controls, 2=wait for build, 3=dancing/selector, 4=wave started
  private isTutorialMode: boolean = false;
  private tutorialStep: number = 0;
  private tutorialOverlayContainer: Phaser.GameObjects.Container | null = null;

  constructor() {
    super({ key: SCENES.GAMEPLAY });
  }

  init(data: GameplaySceneData): void {
    this.scenarioId = data.scenarioId || 'F1';
  }

  create(): void {
    console.log(`[GameplayScene] Starting level: ${this.scenarioId}`);
    SceneRouter.setCurrentScene(SCENES.GAMEPLAY);

    // Check if experiment mode is active (only set when MapScene EXPERIMENT button is used)
    this.isExperimentMode = GameState.isExperimentMode();
    if (this.isExperimentMode) {
      console.log('[GameplayScene] EXPERIMENT MODE ACTIVE');
    }

    // Load level data (double trees in experiment mode)
    this.level = LevelLoader.loadScenario(
      this.scenarioId,
      this.isExperimentMode ? { treeCountMultiplier: 2 } : undefined
    );
    if (!this.level) {
      console.error(`[GameplayScene] Failed to load scenario: ${this.scenarioId}`);
      SceneRouter.goToMap(SCENES.GAMEPLAY);
      return;
    }

    // Initialize game state
    const scenario = this.level.scenario;
    GameState.startNewGame(this.scenarioId, scenario.waves.length);
    
    // Only record attempt if not in experiment mode or tutorial
    if (!this.isExperimentMode && this.scenarioId !== 'Tutorial') {
      ProgressState.recordAttempt(this.scenarioId);
    }

    // Reset local state
    this.isPaused = false;
    this.isGameOver = false;

    // Set up tower availability
    TowerUnlockSystem.resetResearch();
    TowerUnlockSystem.checkUnlocks();
    
    if (this.isExperimentMode) {
      // Experiment mode: unlock all towers
      this.availableTowers = [
        'arrow', 'cannon', 'antiair', 'tesla', 'laser', 'mortar',
        'ice', 'fire', 'hive', 'trap', 'lightning', 'gem'
      ];
    } else {
      // Normal mode: use scenario restrictions
      if (scenario.allowedTowers) {
        TowerUnlockSystem.setScenarioTowers(scenario.allowedTowers);
      }
      
      // For custom levels, apply pre-researched towers
      const customLevel = CustomLevelManager.getLevel(this.scenarioId);
      if (customLevel?.preResearchedTowers) {
        TowerUnlockSystem.setPreResearchedTowers(customLevel.preResearchedTowers);
      }
      
      // Show all displayable towers (including ones that need research)
      this.availableTowers = TowerUnlockSystem.getDisplayableTowers();
    }
    // Initialize all players to the first available tower
    const defaultTower = this.availableTowers[0] || 'arrow';
    this.playerTowerSelections.set('P1', defaultTower);
    this.playerTowerSelections.set('P2', defaultTower);
    this.playerTowerSelections.set('P3', defaultTower);
    this.playerTowerSelections.set('P4', defaultTower);

    // Initialize all systems
    this.initializeSystems();

    // Build the level
    this.buildLevel();

    // Create UI
    this.createUI();

    // Experiment mode: Create spawn panel and disable auto-waves
    if (this.isExperimentMode) {
      WaveSpawner.setAutoStart(false);
      this.createExperimentSpawnPanel();
    } else if (this.scenarioId === 'Tutorial') {
      this.isTutorialMode = true;
      WaveSpawner.setAutoStart(false);
      this.showTutorialStep(0);
    } else {
      // Normal mode: Start wave spawner with auto-start
      WaveSpawner.setAutoStart(true);
      WaveSpawner.setWaveStartDelay(20); // 20 seconds before first wave
    }

    // Set up visibility change handler for auto-pause when tab loses focus
    this.setupVisibilityHandler();
  }

  private setupVisibilityHandler(): void {
    const handleLostFocus = () => {
      // Window/tab lost focus - pause and show pause menu so user can resume when they click back
      if (!this.isPaused && !this.isGameOver && this.scene.isActive()) {
        this.isPaused = true;
        GameState.setPaused(true);
        this.showPauseMenu();
        console.log('[GameplayScene] Auto-paused (window/tab lost focus)');
      }
    };

    this.boundVisibilityHandler = () => {
      if (document.hidden) {
        handleLostFocus();
      }
    };

    this.boundBlurHandler = () => handleLostFocus();

    document.addEventListener('visibilitychange', this.boundVisibilityHandler);
    window.addEventListener('blur', this.boundBlurHandler);
  }

  private initializeSystems(): void {
    if (!this.level) return;

    const scenario = this.level.scenario;

    // Clear EntityManager
    EntityManager.clear();

    // Initialize InputRouter
    InputRouter.init(this, this.level.gridConfig.tileWidth);
    InputRouter.setMenuMode(false); // Normal deadzone for gameplay

    // Initialize RenderSystem
    RenderSystem.init(this, this.level.gridConfig);

    // Initialize PathingSystem and register paths
    PathingSystem.init(this);
    this.registerPaths();

    // Initialize SessionManager with mode and resources
    // Coins are for buying towers, gems are for upgrades
    if (this.isExperimentMode) {
      // Experiment mode: unlimited resources, 100 villagers
      SessionManager.init(
        GameState.getMode(),
        9999,  // Unlimited gems
        100,   // 100 villagers
        9999   // Unlimited coins
      );
    } else {
      const baseCoins = scenario.startingCoins ?? 50;
      const playerCount = GameState.getPlayerCount();
      const coinMultiplier = 1 + (playerCount - 1) * 0.3; // +30% per additional player
      const startingCoins = Math.round(baseCoins * coinMultiplier);
      SessionManager.init(
        GameState.getMode(),
        scenario.startingGems ?? 10,
        scenario.startingVillagers ?? 20,
        startingCoins
      );
    }

    // Set up SessionManager callbacks
    SessionManager.onCoinsChanged = (coins) => this.updateCoinsDisplay(coins);
    SessionManager.onGemsChanged = (gems) => this.updateGemsDisplay(gems);
    SessionManager.onVillagersChanged = (villagers) => this.updateVillagersDisplay(villagers);

    // Initialize DropSystem
    DropSystem.init(this);
    DropSystem.onCoinsCollected = () => {
      // Visual feedback handled by Drop entity
    };
    DropSystem.onGemsCollected = () => {
      // Visual feedback handled by Drop entity
    };

    // Initialize TowerPlacementSystem
    // Towers are purchased with COINS (not gems)
    TowerPlacementSystem.init(this);
    TowerPlacementSystem.setSelectedTowerType(this.playerTowerSelections.get('P1') || 'arrow', 'P1');
    TowerPlacementSystem.setSelectedTowerType(this.playerTowerSelections.get('P2') || 'arrow', 'P2');
    TowerPlacementSystem.setSelectedTowerType(this.playerTowerSelections.get('P3') || 'arrow', 'P3');
    TowerPlacementSystem.setSelectedTowerType(this.playerTowerSelections.get('P4') || 'arrow', 'P4');
    TowerPlacementSystem.setResourceCallbacks(
      (cost) => SessionManager.canAffordCoins(cost),
      (cost) => SessionManager.spendCoins(cost)
    );
    TowerPlacementSystem.onTowerPlaced = (slot, tower, playerId) => {
      SessionManager.recordTowerPlaced(playerId);
      GameState.addTowerBuilt();
    };
    
    // When a tower type is researched (first placement), refresh the selector UI
    TowerPlacementSystem.onTowerResearched = (towerType) => {
      this.refreshTowerSelectorItem(towerType);
      this.showResearchFeedback(towerType);
    };

    // Initialize TowerInteractionSystem
    TowerInteractionSystem.init(this);
    TowerInteractionSystem.onTowerSold = (tower, refund) => {
      console.log(`[GameplayScene] Tower sold for ${refund} coins`);
    };
    TowerInteractionSystem.onTowerUpgraded = (tower, newLevel) => {
      console.log(`[GameplayScene] Tower upgraded to level ${newLevel}`);
    };

    // Initialize WaveSpawner
    WaveSpawner.init(this, this.scenarioId);
    WaveSpawner.setDifficultyByPlayerCount(GameState.getPlayerCount());

    // Set up WaveSpawner callbacks
    WaveSpawner.onWaveStart = (wave, total) => {
      GameState.setCurrentWave(wave);
      this.updateWaveDisplay(wave, total);
      DropSystem.onWaveStart(wave);
      // 5% coin bonus at start of each wave (except wave 1) – reward for saving money
      if (wave > 1) {
        const current = SessionManager.getCoins();
        const newTotal = Math.round(current * 1.05);
        const interest = newTotal - current;
        SessionManager.addCoins(interest);
        if (interest > 0) this.showWaveInterestFeedback(interest);
      }
    };

    WaveSpawner.onMonsterKilled = (monster, _reward) => {
      GameState.addMonsterKill();
      // Track damage dealt (monster's full HP since it was killed, not leaked)
      GameState.addMonsterDamage(monster.getMaxHp());

      // Spawn coin/gem drops from monster (must be picked up manually)
      DropSystem.spawnMonsterDrops(monster.monsterType, monster.x, monster.y);
    };

    WaveSpawner.onMonsterReachedBase = (monster, damage) => {
      if (!DebugSystem.isGodMode()) {
        const remaining = SessionManager.removeVillager(damage);
        if (remaining <= 0) {
          WaveSpawner.triggerDefeat();
        }
      }
    };

    WaveSpawner.onVictory = () => this.handleVictory();
    WaveSpawner.onDefeat = () => this.handleDefeat();
    
    // Track occupied trees for DropSystem wave bonus
    WaveSpawner.onWaveComplete = (_wave, _stats) => {
      // Get set of occupied tree slot IDs
      const occupiedTreeIds = new Set<string>();
      this.treeSlots.forEach((slot, id) => {
        if (slot.isOccupied()) {
          occupiedTreeIds.add(id);
        }
      });
      DropSystem.onWaveEnd(occupiedTreeIds);
      LevelStatsTracker.recordWaveCompleted();
    };

    // Boss callbacks
    WaveSpawner.onBossSpawned = (boss) => this.handleBossSpawned(boss);
    WaveSpawner.onBossKilled = (boss) => this.handleBossKilled(boss);
    WaveSpawner.onBossReachedBase = (boss) => this.handleBossReachedBase(boss);
    WaveSpawner.onBossEatVillager = (damage) => {
      if (!DebugSystem.isGodMode()) {
        const remaining = SessionManager.removeVillager(damage);
        if (remaining <= 0) {
          WaveSpawner.triggerDefeat();
        }
      }
    };

    // Initialize VFXSystem
    VFXSystem.init(this);

    // Initialize AudioRouter for SFX
    AudioRouter.init(this);
    console.log('[GameplayScene] AudioRouter initialized for SFX');

    // Fade out and stop menu music when in a game level
    AudioRouter.stopMenuMusic(this);

    // Initialize LevelStatsTracker
    LevelStatsTracker.startLevel(this.scenarioId);

    // Tower placement callback
    TowerPlacementSystem.onTowerPlaced = (slot, tower, playerId) => {
      SessionManager.recordTowerPlaced(playerId);
      GameState.addTowerBuilt();
      // Track tower cost for stats
      const towerStats = Tower.getStats(tower.towerType);
      LevelStatsTracker.recordTowerBuilt(tower.towerType, towerStats.cost);
      // Tutorial: after "build a tower" step, show dancing/selector step
      if (this.isTutorialMode && this.tutorialStep === 2) {
        this.tutorialStep = 3;
        this.showTutorialStep(3);
      }
    };

    // Initialize Debug System
    DebugSystem.init(this);

    // Create Boss Health Bar
    this.bossHealthBar = new BossHealthBar(this);

    // Create Wave Preview UI
    this.wavePreviewUI = new WavePreviewUI(this);

    // Start background music - randomly pick first track, then play in order
    this.startMusicPlaylist();
  }

  private startMusicPlaylist(): void {
    // Pick a random starting track
    this.currentTrackIndex = Math.floor(Math.random() * this.musicTracks.length);
    this.playCurrentTrack();
  }

  private playCurrentTrack(): void {
    const trackKey = this.musicTracks[this.currentTrackIndex];

    if (!this.cache.audio.exists(trackKey)) {
      console.warn(`[GameplayScene] Music track ${trackKey} not found`);
      return;
    }

    const bpm = AudioRouter.getBPMForTrack(trackKey);
    AudioRouter.setCurrentBPM(bpm, trackKey);
    console.log(`[GameplayScene] Now playing: ${trackKey} (track ${this.currentTrackIndex + 1}/${this.musicTracks.length}, ${bpm} BPM) — dance sync active`);

    if (bpm <= 0 || !Number.isFinite(bpm)) {
      console.error(`[GameplayScene] Invalid BPM for ${trackKey}: ${bpm}. Characters will not dance to the beat.`);
    }

    // Stop previous track if playing
    if (this.bgMusic) {
      this.bgMusic.stop();
      this.bgMusic.destroy();
    }

    // Play new track (not looping - we'll handle sequencing manually)
    this.bgMusic = this.sound.add(trackKey, {
      loop: false,
      volume: SettingsState.getMusicVolume(),
    });

    this.bgMusic.once('complete', () => {
      this.playNextTrack();
    });

    this.bgMusic.play();
  }

  private playNextTrack(): void {
    // Move to next track (wrap around)
    this.currentTrackIndex = (this.currentTrackIndex + 1) % this.musicTracks.length;
    this.playCurrentTrack();
  }

  private handleBossSpawned(boss: Boss): void {
    console.log(`[GameplayScene] Boss spawned: ${boss.bossType}`);
    this.bossHealthBar?.show(boss);
  }

  private handleBossKilled(boss: Boss): void {
    console.log(`[GameplayScene] Boss killed: ${boss.bossType}`);
    // Spawn bonus drops from boss (must be picked up manually)
    DropSystem.spawnBossDrops(boss.x, boss.y);
  }

  private handleBossReachedBase(boss: Boss): void {
    console.log(`[GameplayScene] Boss at base: ${boss.bossType} - eating villagers!`);
  }

  private registerPaths(): void {
    if (!this.level) return;

    for (const pathData of this.level.pathsWorld) {
      if (pathData.routes) {
        // Multi-route path
        PathingSystem.registerMultiRoutePath(
          pathData.id,
          pathData.routes,
          pathData.distribution || {},
          pathData.curved,
          pathData.curveResolution
        );
      } else if (pathData.curved && pathData.points) {
        // Curved single path
        PathingSystem.registerCurvedPath(
          pathData.id,
          pathData.points,
          pathData.curveResolution
        );
      } else if (pathData.points) {
        // Simple path
        PathingSystem.registerPath({
          id: pathData.id,
          points: pathData.points,
          totalLength: pathData.totalLength,
          segmentLengths: pathData.segmentLengths,
        });
      }
    }
  }

  private getEffectiveBiome(): BiomeType {
    if (this.isExperimentMode) {
      return GameState.getSelectedBiome();
    }
    const customLevel = CustomLevelManager.getLevel(this.scenarioId);
    if (customLevel?.biome) {
      return customLevel.biome;
    }
    const region = this.level!.scenario.region;
    if (region === 'swamp') return 'bog';
    if (region === 'ruins') return 'ruins';
    if (region === 'volcano') return 'volcano';
    return 'forest';
  }

  /** Switch biome in experiment mode (ground + path + tree slots + embers). */
  private applyExperimentBiome(biome: BiomeType): void {
    if (!this.isExperimentMode) return;
    GameState.setSelectedBiome(biome);
    const grassKey = biome === 'bog' ? 'grass-bog' : biome === 'ruins' ? 'grass-ruins' : biome === 'volcano' ? 'grass-volcano' : 'grass';
    if (this.groundTileSprite && this.textures.exists(grassKey)) {
      this.groundTileSprite.setTexture(grassKey);
    }
    this.drawPathsWithBiome(biome);
    this.treeSlots.forEach(slot => slot.setBiome(biome));
    if (biome === 'volcano') {
      this.createEmbers();
    } else {
      this.destroyEmbers();
    }
  }

  /** Build a ribbon polygon (left edge + right edge) from centerline and half-width. */
  private buildRibbonPolygon(points: Array<{ x: number; y: number }>, halfWidth: number): Array<{ x: number; y: number }> {
    if (points.length < 2) return [];
    const n = points.length;
    const left: Array<{ x: number; y: number }> = [];
    const right: Array<{ x: number; y: number }> = [];
    for (let i = 0; i < n; i++) {
      const p = points[i];
      const i0 = i === 0 ? 0 : i - 1;
      const i1 = i === n - 1 ? n - 1 : i + 1;
      const dx = points[i1].x - points[i0].x;
      const dy = points[i1].y - points[i0].y;
      const len = Math.sqrt(dx * dx + dy * dy) || 1;
      const perpX = (-dy / len) * halfWidth;
      const perpY = (dx / len) * halfWidth;
      left.push({ x: p.x + perpX, y: p.y + perpY });
      right.push({ x: p.x - perpX, y: p.y - perpY });
    }
    return left.concat(right.reverse());
  }

  /** Number of ribbon layers for smooth blurred-edge mask (BitmapMask respects alpha). */
  private static readonly PATH_MASK_LAYERS = 32;

  /** Draw path mask with many smooth alpha layers into graphics (for RenderTexture → BitmapMask). */
  private drawPathMaskRibbons(g: Phaser.GameObjects.Graphics): void {
    if (!this.level?.pathsWorld.length) return;
    const maxHalfWidth = 42;
    const layers: { halfWidth: number; alpha: number }[] = [];
    for (let i = 0; i < GameplayScene.PATH_MASK_LAYERS; i++) {
      const t = i / (GameplayScene.PATH_MASK_LAYERS - 1);
      const halfWidth = Math.max(1, maxHalfWidth * (1 - t));
      const alpha = 0.02 + 0.48 * t * t;
      layers.push({ halfWidth, alpha });
    }
    const drawFilledPath = (points: Array<{ x: number; y: number }>) => {
      if (points.length < 2) return;
      for (const { halfWidth, alpha } of layers) {
        const poly = this.buildRibbonPolygon(points, halfWidth);
        if (poly.length < 3) continue;
        g.fillStyle(0xffffff, alpha);
        g.beginPath();
        g.moveTo(poly[0].x, poly[0].y);
        for (let i = 1; i < poly.length; i++) g.lineTo(poly[i].x, poly[i].y);
        g.closePath();
        g.fillPath();
      }
    };
    for (const pathData of this.level.pathsWorld) {
      const registeredPath = PathingSystem.getPath(pathData.id);
      if (registeredPath) {
        registeredPath.routes.forEach((route) => drawFilledPath(route.points));
      } else if (pathData.routes) {
        for (const points of Object.values(pathData.routes)) {
          drawFilledPath(points as Array<{ x: number; y: number }>);
        }
      } else if (pathData.points) {
        drawFilledPath(pathData.points);
      }
    }
  }

  /** Destroy volcano embers when leaving volcano biome. */
  private destroyEmbers(): void {
    this.emberTweens.forEach(t => t.stop());
    this.emberTweens.length = 0;
    if (this.emberContainer) {
      this.emberContainer.destroy();
      this.emberContainer = null;
    }
  }

  /** Create subtle floating embers for volcano biome (tiny black/glowing specs). */
  private createEmbers(): void {
    this.destroyEmbers();
    if (!this.level) return;
    const { gridConfig } = this.level;
    const w = Math.max(gridConfig.mapWidthTiles * gridConfig.tileWidth, DESIGN_WIDTH) + 200;
    const h = Math.max(gridConfig.mapHeightTiles * gridConfig.tileHeight, DESIGN_HEIGHT) + 200;
    const count = 48;
    this.emberContainer = this.add.container(0, 0);
    this.emberContainer.setDepth(RENDER_LAYERS.PATH + 1);
    for (let i = 0; i < count; i++) {
      const x = Math.random() * w;
      const y = Math.random() * h;
      const g = this.add.graphics();
      g.setPosition(x, y);
      const glowRadius = 2.6 + Math.random() * 2.6;   // ~30% bigger (was 2–4)
      const coreRadius = 0.78 + Math.random() * 0.65; // ~30% bigger (was 0.6–1.1)
      g.fillStyle(0xff4400, 0.2 + Math.random() * 0.26); // ~30% brighter glow (was 0.15–0.35)
      g.fillCircle(0, 0, glowRadius);
      g.fillStyle(0x1a0808, 0.9);
      g.fillCircle(0, 0, coreRadius);
      this.emberContainer.add(g);
      // Move around more: wider horizontal and vertical drift
      const dx = (Math.random() - 0.5) * 100;
      const dy = -40 - Math.random() * 60;
      const duration = 4000 + Math.random() * 6000;
      const delay = Math.random() * 4000;
      const moveTween = this.tweens.add({
        targets: g,
        x: x + dx,
        y: y + dy,
        duration,
        delay,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      this.emberTweens.push(moveTween);
      // Slow fade in and out (~30% brighter range)
      g.setAlpha(0.32 + Math.random() * 0.26);
      const fadeDuration = 2500 + Math.random() * 2000;
      const fadeDelay = Math.random() * 2000;
      const fadeTween = this.tweens.add({
        targets: g,
        alpha: { from: 0.26, to: 0.85 },
        duration: fadeDuration,
        delay: fadeDelay,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      this.emberTweens.push(fadeTween);
    }
  }

  /** Path texture key per biome (masked path image); fallback to stroke if missing. */
  private getPathTextureKey(biome: BiomeType): string | null {
    const keys: Record<BiomeType, string> = {
      forest: 'grass-path',
      bog: 'bog-path',
      ruins: 'ruins-path',
      volcano: 'volcano-path',
    };
    const key = keys[biome];
    return this.textures.exists(key) ? key : null;
  }

  /** Draw or redraw path: masked path image when texture exists, else stroke. */
  private drawPathsWithBiome(biome: BiomeType): void {
    const pathsWorld = this.level?.pathsWorld;
    if (!pathsWorld?.length) return;

    const pathTextureKey = this.getPathTextureKey(biome);
    if (pathTextureKey) {
      // Path image masked by blurred path shape (all biomes)
      if (this.pathGraphics) this.pathGraphics.clear();
      if (this.pathMaskImage) {
        this.pathMaskImage.destroy();
        this.pathMaskImage = null;
      }
      if (this.pathMaskTexture) {
        this.pathMaskTexture.destroy();
        this.pathMaskTexture = null;
      }
      if (!this.level) return;
      const { gridConfig } = this.level;
      const groundWidth = Math.max(gridConfig.mapWidthTiles * gridConfig.tileWidth, DESIGN_WIDTH) + 200;
      const groundHeight = Math.max(gridConfig.mapHeightTiles * gridConfig.tileHeight, DESIGN_HEIGHT) + 200;
      const maskGraphics = this.add.graphics();
      this.drawPathMaskRibbons(maskGraphics);
      this.pathMaskTexture = this.add.renderTexture(0, 0, groundWidth, groundHeight);
      this.pathMaskTexture.setOrigin(0, 0);
      this.pathMaskTexture.draw(maskGraphics, 0, 0);
      maskGraphics.destroy();
      this.pathMaskTexture.setPosition(groundWidth / 2, groundHeight / 2);
      this.pathMaskTexture.setOrigin(0.5, 0.5);
      this.pathMaskTexture.setVisible(false);
      this.pathMaskImage = this.add.tileSprite(
        groundWidth / 2,
        groundHeight / 2,
        groundWidth,
        groundHeight,
        pathTextureKey
      );
      this.pathMaskImage.setDepth(RENDER_LAYERS.PATH);
      this.pathMaskImage.setAlpha(0.6); // 60% opacity so background shows through
      this.pathMaskImage.setMask(new Phaser.Display.Masks.BitmapMask(this, this.pathMaskTexture));
      return;
    }

    // Fallback: stroke path when no path texture for this biome
    if (this.pathMaskImage) {
      this.pathMaskImage.destroy();
      this.pathMaskImage = null;
    }
    if (this.pathMaskTexture) {
      this.pathMaskTexture.destroy();
      this.pathMaskTexture = null;
    }
    if (!this.pathGraphics) return;
    const pathGraphics = this.pathGraphics;
    pathGraphics.clear();

    const PATH_COLOR_OUTER = biome === 'bog' ? 0x2a2015 : biome === 'volcano' ? 0x2a1515 : 0x5a5348;
    const PATH_COLOR_MID   = biome === 'bog' ? 0x352a1a : biome === 'volcano' ? 0x3a1a1a : 0x6b6358;
    const PATH_COLOR_INNER = biome === 'bog' ? 0x423520 : biome === 'volcano' ? 0x4a2525 : 0x7a7268;

    const drawCurvedPath = (points: Array<{ x: number; y: number }>) => {
      if (points.length < 2) return;
      pathGraphics.lineStyle(84, PATH_COLOR_OUTER, 0.18);
      pathGraphics.beginPath();
      pathGraphics.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) pathGraphics.lineTo(points[i].x, points[i].y);
      pathGraphics.strokePath();
      pathGraphics.lineStyle(66, PATH_COLOR_OUTER, 0.25);
      pathGraphics.beginPath();
      pathGraphics.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) pathGraphics.lineTo(points[i].x, points[i].y);
      pathGraphics.strokePath();
      pathGraphics.lineStyle(48, PATH_COLOR_MID, 0.35);
      pathGraphics.beginPath();
      pathGraphics.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) pathGraphics.lineTo(points[i].x, points[i].y);
      pathGraphics.strokePath();
      pathGraphics.lineStyle(30, PATH_COLOR_INNER, 0.5);
      pathGraphics.beginPath();
      pathGraphics.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) pathGraphics.lineTo(points[i].x, points[i].y);
      pathGraphics.strokePath();
    };

    for (const pathData of pathsWorld) {
      const registeredPath = PathingSystem.getPath(pathData.id);
      if (registeredPath) {
        registeredPath.routes.forEach((route) => drawCurvedPath(route.points));
      } else if (pathData.routes) {
        for (const points of Object.values(pathData.routes)) {
          drawCurvedPath(points as Array<{ x: number; y: number }>);
        }
      } else if (pathData.points) {
        drawCurvedPath(pathData.points);
      }
    }
  }

  private buildLevel(): void {
    if (!this.level) return;

    const effectiveBiome = this.getEffectiveBiome();
    const { gridConfig, baseWorldPos, playerSpawnWorldPos, treeSlotWorldPos, pathsWorld } = this.level;

    // Draw ground - use max of world size and screen size to ensure full coverage
    const worldWidth = gridConfig.mapWidthTiles * gridConfig.tileWidth;
    const worldHeight = gridConfig.mapHeightTiles * gridConfig.tileHeight;
    const groundWidth = Math.max(worldWidth, DESIGN_WIDTH) + 200; // Extra padding
    const groundHeight = Math.max(worldHeight, DESIGN_HEIGHT) + 200;

    const grassKey = effectiveBiome === 'bog' ? 'grass-bog' : effectiveBiome === 'ruins' ? 'grass-ruins' : effectiveBiome === 'volcano' ? 'grass-volcano' : 'grass';
    if (this.textures.exists(grassKey)) {
      this.groundTileSprite = this.add.tileSprite(
        groundWidth / 2,
        groundHeight / 2,
        groundWidth,
        groundHeight,
        grassKey
      );
      this.groundTileSprite.setDepth(RENDER_LAYERS.GROUND);
    } else if (this.textures.exists('grass')) {
      this.groundTileSprite = this.add.tileSprite(
        groundWidth / 2,
        groundHeight / 2,
        groundWidth,
        groundHeight,
        'grass'
      );
      this.groundTileSprite.setDepth(RENDER_LAYERS.GROUND);
    } else {
      this.groundTileSprite = null;
      // Fallback: Region-based ground color
      const regionColors: Record<string, number> = {
        forest: 0x1a2e1a,
        swamp: 0x1a1a2e,
        ruins: 0x2e1a1a,
        volcano: 0x2e1a2e,
      };
      const groundColor = regionColors[this.level.scenario.region] || COLORS.BG_DARK;

      this.groundTileSprite = null;
      const ground = this.add.rectangle(groundWidth / 2, groundHeight / 2, groundWidth, groundHeight, groundColor);
      ground.setDepth(RENDER_LAYERS.GROUND);
    }

    // Draw paths with curved spline and blurred brown look (stored so experiment biome can redraw)
    if (this.pathGraphics) {
      this.pathGraphics.destroy();
      this.pathGraphics = null;
    }
    if (this.pathMaskImage) {
      this.pathMaskImage.destroy();
      this.pathMaskImage = null;
    }
    if (this.pathMaskTexture) {
      this.pathMaskTexture.destroy();
      this.pathMaskTexture = null;
    }
    this.destroyEmbers();
    this.pathGraphics = this.add.graphics();
    this.pathGraphics.setDepth(RENDER_LAYERS.PATH);
    this.drawPathsWithBiome(effectiveBiome);
    if (effectiveBiome === 'volcano') {
      this.createEmbers();
    }

    // Create tree slots and register with DropSystem
    this.treeSlots.clear();
    treeSlotWorldPos.forEach((slot, i) => {
      const treeSlot = new TreeSlot(this, {
        id: `slot-${i}`,
        slotId: slot.id,
        x: slot.x,
        y: slot.y,
        biome: effectiveBiome,
      });
      EntityManager.add(treeSlot);

      // Register tree for proximity-based drops
      const treeId = `slot-${i}`;
      this.treeSlots.set(treeId, treeSlot);
      DropSystem.registerTree(treeId);
    });

    // Create base
    const base = new Base(this, {
      id: 'base',
      x: baseWorldPos.x,
      y: baseWorldPos.y,
      startingVillagers: SessionManager.getVillagers(),
    });
    EntityManager.add(base);

    // Connect SessionManager to Base entity for villager sync
    SessionManager.setBaseEntity(base);

    // Create players
    // Players spawn 1 tile offset from the spawn point (so they're not on top of each other)
    const tileSize = this.level?.gridConfig.tileWidth || 90;
    const enabledPlayers = SessionManager.getEnabledPlayers();
    enabledPlayers.forEach((playerId, index) => {
      let spawnX: number;
      let spawnY: number;
      
      // Get spawn position - fallback to base if no spawns defined
      const spawnPoint = playerSpawnWorldPos[index] || playerSpawnWorldPos[0];
      if (spawnPoint) {
        // Spawn 1 tile offset from the spawn point (spread players out)
        const offsetX = (index % 2 === 0 ? -1 : 1) * tileSize * 0.5;
        const offsetY = (index < 2 ? -1 : 1) * tileSize * 0.3;
        spawnX = spawnPoint.x + offsetX;
        spawnY = spawnPoint.y + offsetY;
      } else {
        // No player spawns defined - spawn at base
        console.warn(`[GameplayScene] No player spawn defined, using base position`);
        spawnX = baseWorldPos.x + (index % 2 === 0 ? -1 : 1) * tileSize * 0.5;
        spawnY = baseWorldPos.y + (index < 2 ? -1 : 1) * tileSize * 0.3;
      }
      
      const player = new Player(this, {
        id: `player-${index + 1}`,
        playerId,
        x: spawnX,
        y: spawnY,
      });
      EntityManager.add(player);
      SessionManager.registerPlayerEntity(playerId, player.id);
    });

    // Center camera on base area
    RenderSystem.centerOn(baseWorldPos.x - 200, baseWorldPos.y);
  }

  private createUI(): void {
    // Wave indicator (top center)
    const waveBg = this.add.rectangle(DESIGN_WIDTH / 2, 50, 225, 60, COLORS.BG_MEDIUM, 0.8);
    waveBg.setStrokeStyle(3, COLORS.PRIMARY);
    waveBg.setScrollFactor(0);
    waveBg.setDepth(RENDER_LAYERS.UI_SCREEN);

    this.waveText = this.add.text(DESIGN_WIDTH / 2, 50, `Wave 0/${WaveSpawner.getTotalWaves()}`, {
      fontSize: '27px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    this.waveText.setOrigin(0.5);
    this.waveText.setScrollFactor(0);
    this.waveText.setDepth(RENDER_LAYERS.UI_SCREEN + 1);

    // Region indicator (top center, below wave)
    this.regionText = this.add.text(DESIGN_WIDTH / 2, 85, this.level?.scenario.region.toUpperCase() || '', {
      fontSize: '15px',
      color: '#888888',
    });
    this.regionText.setOrigin(0.5);
    this.regionText.setScrollFactor(0);
    this.regionText.setDepth(RENDER_LAYERS.UI_SCREEN + 1);

    // Skip wave button (right of wave indicator)
    const skipBtn = this.add.rectangle(DESIGN_WIDTH / 2 + 150, 50, 75, 45, COLORS.BG_MEDIUM, 0.8);
    skipBtn.setStrokeStyle(3, COLORS.PRIMARY);
    skipBtn.setScrollFactor(0);
    skipBtn.setDepth(RENDER_LAYERS.UI_SCREEN);
    skipBtn.setInteractive({ useHandCursor: true });

    const skipText = this.add.text(DESIGN_WIDTH / 2 + 150, 50, '▶▶', {
      fontSize: '21px',
      color: '#D8A43A', // Tiki Gold
      fontStyle: 'bold',
    });
    skipText.setOrigin(0.5);
    skipText.setScrollFactor(0);
    skipText.setDepth(RENDER_LAYERS.UI_SCREEN + 1);

    skipBtn.on('pointerover', () => {
      skipBtn.setStrokeStyle(4, COLORS.NEON_ICE);
      skipText.setColor('#A6F2FF');
    });
    skipBtn.on('pointerout', () => {
      skipBtn.setStrokeStyle(3, COLORS.PRIMARY);
      skipText.setColor('#D8A43A');
    });
    skipBtn.on('pointerdown', () => {
      this.forceNextWave();
    });

    // Gems display (top left)
    const gemsBg = this.add.rectangle(82, 50, 120, 52, COLORS.BG_MEDIUM, 0.8);
    gemsBg.setStrokeStyle(3, COLORS.ACCENT);
    gemsBg.setScrollFactor(0);
    gemsBg.setDepth(RENDER_LAYERS.UI_SCREEN);

    // Gem icon
    if (this.textures.exists('gem-1')) {
      const gemIcon = this.add.image(37, 50, 'gem-1');
      gemIcon.setScale(0.24);
      gemIcon.setScrollFactor(0);
      gemIcon.setDepth(RENDER_LAYERS.UI_SCREEN + 1);
    } else {
      this.add.text(37, 50, '💎', { fontSize: '27px' }).setOrigin(0.5).setScrollFactor(0).setDepth(RENDER_LAYERS.UI_SCREEN + 1);
    }

    this.gemsText = this.add.text(97, 50, SessionManager.getGems().toString(), {
      fontSize: '24px',
      color: '#ff88ff',
      fontStyle: 'bold',
    });
    this.gemsText.setOrigin(0.5);
    this.gemsText.setScrollFactor(0);
    this.gemsText.setDepth(RENDER_LAYERS.UI_SCREEN + 1);

    // Coins display (top left, next to gems)
    const coinsBg = this.add.rectangle(217, 50, 120, 52, COLORS.BG_MEDIUM, 0.8);
    coinsBg.setStrokeStyle(3, COLORS.PRIMARY);
    coinsBg.setScrollFactor(0);
    coinsBg.setDepth(RENDER_LAYERS.UI_SCREEN);

    // Coin icon
    if (this.textures.exists('coin-1')) {
      const coinIcon = this.add.image(172, 50, 'coin-1');
      coinIcon.setScale(0.24);
      coinIcon.setScrollFactor(0);
      coinIcon.setDepth(RENDER_LAYERS.UI_SCREEN + 1);
    } else {
      this.add.text(172, 50, '🪙', { fontSize: '27px' }).setOrigin(0.5).setScrollFactor(0).setDepth(RENDER_LAYERS.UI_SCREEN + 1);
    }

    this.coinsText = this.add.text(232, 50, SessionManager.getCoins().toString(), {
      fontSize: '24px',
      color: '#D8A43A', // Tiki Gold - currency
      fontStyle: 'bold',
    });
    this.coinsText.setOrigin(0.5);
    this.coinsText.setScrollFactor(0);
    this.coinsText.setDepth(RENDER_LAYERS.UI_SCREEN + 1);

    // Villagers display (top right) — villager sprite + count
    const villagersBg = this.add.rectangle(DESIGN_WIDTH - 120, 50, 150, 52, COLORS.BG_MEDIUM, 0.8);
    villagersBg.setStrokeStyle(3, COLORS.SECONDARY);
    villagersBg.setScrollFactor(0);
    villagersBg.setDepth(RENDER_LAYERS.UI_SCREEN);

    const villagerIconKey = this.textures.exists('villager-b1') ? 'villager-b1' : 'villager-b2';
    const villagerIcon = this.add.image(DESIGN_WIDTH - 172, 35, villagerIconKey);
    villagerIcon.setDisplaySize(80, 80);
    villagerIcon.setOrigin(0.5, 0.5);
    villagerIcon.setScrollFactor(0);
    villagerIcon.setDepth(RENDER_LAYERS.UI_SCREEN + 1);

    this.villagersText = this.add.text(DESIGN_WIDTH - 90, 50, SessionManager.getVillagers().toString(), {
      fontSize: '27px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    this.villagersText.setOrigin(0.5);
    this.villagersText.setScrollFactor(0);
    this.villagersText.setDepth(RENDER_LAYERS.UI_SCREEN + 1);

    // Monsters remaining (top right, below villagers)
    const monstersBg = this.add.rectangle(DESIGN_WIDTH - 120, 110, 150, 45, COLORS.BG_MEDIUM, 0.6);
    monstersBg.setStrokeStyle(2, COLORS.DANGER);
    monstersBg.setScrollFactor(0);
    monstersBg.setDepth(RENDER_LAYERS.UI_SCREEN);

    this.add.text(DESIGN_WIDTH - 172, 110, '👾', { fontSize: '21px' }).setOrigin(0.5).setScrollFactor(0).setDepth(RENDER_LAYERS.UI_SCREEN + 1);

    this.monstersText = this.add.text(DESIGN_WIDTH - 90, 110, '0', {
      fontSize: '21px',
      color: '#ff6b6b',
    });
    this.monstersText.setOrigin(0.5);
    this.monstersText.setScrollFactor(0);
    this.monstersText.setDepth(RENDER_LAYERS.UI_SCREEN + 1);

    // Tower selector (bottom center)
    this.createTowerSelector();

    // Mode indicator (for 2P/3P/4P)
    const playerCount = GameState.getPlayerCount();
    if (playerCount > 1) {
      const modeLabel = `${playerCount} PLAYER MODE`;
      this.add.text(DESIGN_WIDTH / 2, 15, modeLabel, {
        fontSize: '18px',
        color: '#00ff00',
      }).setOrigin(0.5).setScrollFactor(0).setDepth(RENDER_LAYERS.UI_SCREEN);
    }

    // Music play/pause button (lower right)
    const musicBtnX = DESIGN_WIDTH - 70;
    const musicBtnY = DESIGN_HEIGHT - 70;
    this.musicButtonBg = this.add.rectangle(musicBtnX, musicBtnY, 80, 50, COLORS.BG_MEDIUM, 0.9);
    this.musicButtonBg.setStrokeStyle(2, COLORS.PRIMARY);
    this.musicButtonBg.setOrigin(0.5);
    this.musicButtonBg.setScrollFactor(0);
    this.musicButtonBg.setDepth(RENDER_LAYERS.UI_SCREEN);
    this.musicButtonBg.setInteractive({ useHandCursor: true });
    this.musicButtonText = this.add.text(musicBtnX, musicBtnY, '🔊', {
      fontSize: '24px',
    });
    this.musicButtonText.setOrigin(0.5);
    this.musicButtonText.setScrollFactor(0);
    this.musicButtonText.setDepth(RENDER_LAYERS.UI_SCREEN + 1);
    this.musicButtonBg.on('pointerover', () => {
      this.musicButtonBg?.setStrokeStyle(3, 0xffffff);
    });
    this.musicButtonBg.on('pointerout', () => {
      this.musicButtonBg?.setStrokeStyle(2, COLORS.PRIMARY);
    });
    this.musicButtonBg.on('pointerdown', () => {
      this.toggleMusic();
    });
  }

  // Store references for research UI updates
  private towerSelectorIcons: Map<TowerType, Phaser.GameObjects.Image | Phaser.GameObjects.Rectangle> = new Map();
  private towerSelectorCostTexts: Map<TowerType, Phaser.GameObjects.Text> = new Map(); // Coin cost text
  private towerSelectorGemTexts: Map<TowerType, Phaser.GameObjects.Text> = new Map();  // Gem cost text (for unlockable)
  private towerSelectorGemIcons: Map<TowerType, Phaser.GameObjects.Image> = new Map();
  private towerSelectorCostIcons: Map<TowerType, Phaser.GameObjects.Image> = new Map();
  private towerSelectorOverlays: Map<TowerType, Phaser.GameObjects.Rectangle> = new Map();
  private towerSelectorUnlockLabels: Map<TowerType, Phaser.GameObjects.Text> = new Map();

  private createTowerSelector(): void {
    const towers = this.availableTowers; // Show all displayable towers (including unresearched)
    const spacing = Math.min(100, (DESIGN_WIDTH - 100) / towers.length); // Dynamic spacing to fit all
    const startX = DESIGN_WIDTH / 2 - ((towers.length - 1) * spacing) / 2;
    const y = DESIGN_HEIGHT - 58;

    this.towerSelectorBgs = [];
    this.towerSelectorTexts = [];
    this.towerSelectorTicks.clear();
    this.towerSelectorIcons.clear();
    this.towerSelectorCostTexts.clear();
    this.towerSelectorGemTexts.clear();
    this.towerSelectorGemIcons.clear();
    this.towerSelectorCostIcons.clear();
    this.towerSelectorOverlays.clear();
    this.towerSelectorUnlockLabels.clear();

    // Player colors for triangle indicators: P1=Blue, P2=Red, P3=Yellow, P4=Green
    const playerColors: Record<string, number> = {
      'P1': 0x4444ff,
      'P2': 0xff4444,
      'P3': 0xffff44,
      'P4': 0x44ff44,
    };

    towers.forEach((type, i) => {
      const x = startX + i * spacing;
      const stats = Tower.getStats(type);
      const needsResearch = !this.isExperimentMode && TowerUnlockSystem.needsResearch(type);
      const researchCost = TowerUnlockSystem.getResearchCost(type);

      const bgWidth = Math.min(80, spacing - 10);
      const bg = this.add.rectangle(x, y, bgWidth, 75, COLORS.BG_MEDIUM, 0.8);
      bg.setStrokeStyle(1, needsResearch ? 0x666666 : COLORS.BG_LIGHT);
      bg.setInteractive({ useHandCursor: true });
      bg.setScrollFactor(0);
      bg.setDepth(RENDER_LAYERS.UI_SCREEN);
      bg.setData('type', type);
      bg.setData('baseX', x);

      bg.on('pointerdown', () => {
        this.handleTowerSelectorClick(type, 'P1');
      });

      this.towerSelectorBgs.push(bg);

      // Create downward-pointing triangle indicators for each player
      const triangleSize = 20;
      const ticks: Phaser.GameObjects.Polygon[] = [];
      const halfBgHeight = 75 / 2;

      (['P1', 'P2', 'P3', 'P4'] as const).forEach(pid => {
        // Triangle points centered at origin: top-left, top-right, bottom-center
        const trianglePoints = [
          -triangleSize / 2, -triangleSize / 3,   // top-left
          triangleSize / 2, -triangleSize / 3,    // top-right
          0, triangleSize * 2 / 3,                // bottom-center
        ];
        const tri = this.add.polygon(x, y - halfBgHeight - triangleSize / 4, trianglePoints, playerColors[pid]);
        tri.setOrigin(0.5, 0.5);
        tri.setStrokeStyle(2, 0xffffff);
        tri.setScrollFactor(0);
        tri.setDepth(RENDER_LAYERS.UI_SCREEN + 3);
        tri.setVisible(false);
        tri.setData('playerId', pid);
        ticks.push(tri);
      });

      this.towerSelectorTicks.set(type, ticks);

      // Tower icon
      const iconKey = `icon-${type}`;
      let icon: Phaser.GameObjects.Image | Phaser.GameObjects.Rectangle;
      if (this.textures.exists(iconKey)) {
        icon = this.add.image(x, y - 10, iconKey);
        (icon as Phaser.GameObjects.Image).setScale(0.25);
      } else {
        icon = this.add.rectangle(x, y - 12, 45, 45, stats.color);
      }
      icon.setScrollFactor(0);
      icon.setDepth(RENDER_LAYERS.UI_SCREEN + 1);
      if (needsResearch) {
        const canAffordFirstTime = SessionManager.canAffordGems(researchCost) && SessionManager.canAffordCoins(stats.cost);
        icon.setAlpha(canAffordFirstTime ? 1 : 0.4);
      }
      this.towerSelectorIcons.set(type, icon);

      // Dark overlay for unresearched towers (hidden when affordable so icon is un-ghosted)
      if (needsResearch) {
        const overlay = this.add.rectangle(x, y, bgWidth - 4, 71, 0x000000, 0.5);
        overlay.setScrollFactor(0);
        overlay.setDepth(RENDER_LAYERS.UI_SCREEN + 1);
        const canAffordFirstTime = SessionManager.canAffordGems(researchCost) && SessionManager.canAffordCoins(stats.cost);
        overlay.setVisible(!canAffordFirstTime);
        this.towerSelectorOverlays.set(type, overlay);
      }

      // Cost display - show both gem + coin for unresearched, just coin for researched
      if (needsResearch) {
        const canAffordGems = SessionManager.canAffordGems(researchCost);
        const gemText = this.add.text(x - 22, y + 22, `${researchCost}`, {
          fontSize: '14px',
          color: canAffordGems ? '#aa88ff' : '#ff4444',
        });
        gemText.setOrigin(0.5);
        gemText.setScrollFactor(0);
        gemText.setDepth(RENDER_LAYERS.UI_SCREEN + 2);
        this.towerSelectorGemTexts.set(type, gemText);
        
        if (this.textures.exists('gem-1')) {
          const gemIcon = this.add.image(x - 5, y + 22, 'gem-1');
          gemIcon.setScale(0.09);
          gemIcon.setScrollFactor(0);
          gemIcon.setDepth(RENDER_LAYERS.UI_SCREEN + 2);
          this.towerSelectorGemIcons.set(type, gemIcon);
        }
        
        const canAffordCoins = SessionManager.canAffordCoins(stats.cost);
        const coinText = this.add.text(x + 8, y + 34, `${stats.cost}`, {
          fontSize: '14px',
          color: canAffordCoins ? '#ffd700' : '#ff4444',
        });
        coinText.setOrigin(0.5);
        coinText.setScrollFactor(0);
        coinText.setDepth(RENDER_LAYERS.UI_SCREEN + 2);
        this.towerSelectorCostTexts.set(type, coinText);
        this.towerSelectorTexts.push(coinText);
        
        if (this.textures.exists('coin-1')) {
          const coinIcon = this.add.image(x + 26, y + 34, 'coin-1');
          coinIcon.setScale(0.09);
          coinIcon.setScrollFactor(0);
          coinIcon.setDepth(RENDER_LAYERS.UI_SCREEN + 2);
          this.towerSelectorCostIcons.set(type, coinIcon);
        }
      } else {
        const canAffordCoins = SessionManager.canAffordCoins(stats.cost);
        const costText = this.add.text(x - 10, y + 27, `${stats.cost}`, {
          fontSize: '18px',
          color: canAffordCoins ? '#ffd700' : '#ff4444',
        });
        costText.setOrigin(0.5);
        costText.setScrollFactor(0);
        costText.setDepth(RENDER_LAYERS.UI_SCREEN + 2);
        this.towerSelectorCostTexts.set(type, costText);
        this.towerSelectorTexts.push(costText);
        
        if (this.textures.exists('coin-1')) {
          const costIcon = this.add.image(x + 18, y + 27, 'coin-1');
          costIcon.setScale(0.12);
          costIcon.setScrollFactor(0);
          costIcon.setDepth(RENDER_LAYERS.UI_SCREEN + 2);
          this.towerSelectorCostIcons.set(type, costIcon);
        }
      }

      // "UNLOCK" label for unresearched towers (stored so we can hide after research)
      if (needsResearch) {
        const unlockLabel = this.add.text(x, y - 32, 'UNLOCK', {
          fontSize: '9px',
          color: '#aa88ff',
          fontStyle: 'bold',
        });
        unlockLabel.setOrigin(0.5);
        unlockLabel.setScrollFactor(0);
        unlockLabel.setDepth(RENDER_LAYERS.UI_SCREEN + 2);
        this.towerSelectorUnlockLabels.set(type, unlockLabel);
      }

      // Keybind hint
      const keybind = this.add.text(x, y - 45, `${i + 1}`, {
        fontSize: '15px',
        color: needsResearch ? '#444444' : '#888888',
      });
      keybind.setOrigin(0.5);
      keybind.setScrollFactor(0);
      keybind.setDepth(RENDER_LAYERS.UI_SCREEN + 1);
    });
    
    this.updateTowerSelector();
  }

  private handleTowerSelectorClick(type: TowerType, playerId: PlayerId): void {
    // Just select the tower - research happens when placing, not when selecting
    this.playerTowerSelections.set(playerId, type);
    TowerPlacementSystem.setSelectedTowerType(type, playerId);
    this.updateTowerSelector();
  }

  private refreshTowerSelectorItem(_towerType: TowerType): void {
    // Recreate the entire tower selector so the newly researched tower appears like starters:
    // no gem requirement, full opacity, no UNLOCK label — coins only, same look as pre-unlocked towers
    this.destroyTowerSelector();
    this.createTowerSelector();
    this.updateTowerSelector();
  }

  private destroyTowerSelector(): void {
    // Clean up all tower selector elements
    this.towerSelectorBgs.forEach(bg => bg.destroy());
    this.towerSelectorBgs = [];
    
    this.towerSelectorTexts.forEach(text => text.destroy());
    this.towerSelectorTexts = [];
    
    this.towerSelectorTicks.forEach(ticks => ticks.forEach(t => t.destroy()));
    this.towerSelectorTicks.clear();
    
    this.towerSelectorIcons.forEach(icon => icon.destroy());
    this.towerSelectorIcons.clear();
    
    this.towerSelectorCostTexts.forEach(text => text.destroy());
    this.towerSelectorCostTexts.clear();
    
    this.towerSelectorGemTexts.forEach(text => text.destroy());
    this.towerSelectorGemTexts.clear();
    
    this.towerSelectorGemIcons.forEach(icon => icon.destroy());
    this.towerSelectorGemIcons.clear();
    
    this.towerSelectorCostIcons.forEach(icon => icon.destroy());
    this.towerSelectorCostIcons.clear();
    
    this.towerSelectorOverlays.forEach(overlay => overlay.destroy());
    this.towerSelectorOverlays.clear();
    this.towerSelectorUnlockLabels.forEach(text => text.destroy());
    this.towerSelectorUnlockLabels.clear();
  }

  private showResearchFeedback(type: TowerType): void {
    const stats = Tower.getStats(type);
    const text = this.add.text(DESIGN_WIDTH / 2, DESIGN_HEIGHT - 150, 
      `${type.toUpperCase()} UNLOCKED!`, {
      fontSize: '24px',
      color: '#aa88ff',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 4,
    });
    text.setOrigin(0.5);
    text.setScrollFactor(0);
    text.setDepth(RENDER_LAYERS.UI_SCREEN + 10);
    
    this.tweens.add({
      targets: text,
      y: DESIGN_HEIGHT - 200,
      alpha: 0,
      duration: 1500,
      ease: 'Power2',
      onComplete: () => text.destroy(),
    });
  }

  private showMessage(x: number, y: number, message: string, color: string): void {
    const text = this.add.text(x, y, message, {
      fontSize: '18px',
      color: color,
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3,
    });
    text.setOrigin(0.5);
    text.setScrollFactor(0);
    text.setDepth(RENDER_LAYERS.UI_SCREEN + 10);
    
    this.tweens.add({
      targets: text,
      y: y - 30,
      alpha: 0,
      duration: 1000,
      ease: 'Power2',
      onComplete: () => text.destroy(),
    });
  }

  private updateTowerSelector(): void {
    const playerCount = GameState.getPlayerCount();
    const triangleSize = 20;
    const triangleSpacing = triangleSize + 4;

    // Find the bg for each tower type to get the base X position
    const bgXByType: Map<string, number> = new Map();
    this.towerSelectorBgs.forEach(bg => {
      const type = bg.getData('type') as string;
      const baseX = bg.getData('baseX') as number;
      bgXByType.set(type, baseX);
    });

    // Update triangle visibility and positioning for each tower type
    this.towerSelectorTicks.forEach((ticks, towerType) => {
      const baseX = bgXByType.get(towerType) || 0;
      const baseY = DESIGN_HEIGHT - 58 - 75 / 2 - triangleSize / 4;

      // Find which players have this tower selected
      const playersOnTower: PlayerId[] = [];
      (['P1', 'P2', 'P3', 'P4'] as PlayerId[]).forEach((pid, idx) => {
        // P1 always active, others depend on player count
        const isPlayerActive = idx < playerCount;
        if (isPlayerActive && this.playerTowerSelections.get(pid) === towerType) {
          playersOnTower.push(pid);
        }
      });

      // Hide all ticks first
      ticks.forEach(t => t.setVisible(false));

      // Position visible triangles based on count
      const count = playersOnTower.length;
      if (count === 0) return;

      // Calculate total width and starting offset
      const totalWidth = (count - 1) * triangleSpacing;
      const startOffset = -totalWidth / 2;

      playersOnTower.forEach((pid, i) => {
        const tickIdx = ['P1', 'P2', 'P3', 'P4'].indexOf(pid);
        if (tickIdx >= 0 && tickIdx < ticks.length) {
          const tri = ticks[tickIdx];
          const xOffset = startOffset + i * triangleSpacing;
          tri.setPosition(baseX + xOffset, baseY);
          tri.setVisible(true);
        }
      });
    });
    
    // Update cost text colors and locked/unlocked visuals from current research state
    this.availableTowers.forEach((type) => {
      const stats = Tower.getStats(type);
      const needsResearch = !this.isExperimentMode && TowerUnlockSystem.needsResearch(type);
      const gemCost = TowerUnlockSystem.getResearchCost(type);
      const canAffordGems = SessionManager.canAffordGems(gemCost);
      const canAffordCoins = SessionManager.canAffordCoins(stats.cost);
      const canAffordFirstTime = canAffordGems && canAffordCoins;

      // Un-ghost icon when unlocked or when enough gems+coins for first-time purchase
      const showUnghosted = !needsResearch || canAffordFirstTime;
      const icon = this.towerSelectorIcons.get(type);
      if (icon) {
        icon.setAlpha(showUnghosted ? 1 : 0.4);
      }
      const overlay = this.towerSelectorOverlays.get(type);
      if (overlay) {
        overlay.setVisible(needsResearch && !canAffordFirstTime);
      }
      // Border: dim when locked and not affordable, normal when unlocked or affordable
      const bg = this.towerSelectorBgs.find(b => b.getData('type') === type);
      if (bg) {
        bg.setStrokeStyle(1, showUnghosted ? COLORS.BG_LIGHT : 0x666666);
      }

      const coinText = this.towerSelectorCostTexts.get(type);
      if (coinText) {
        coinText.setColor(canAffordCoins ? '#D8A43A' : '#FF4444');
      }

      // Only show gem cost when tower still needs first-time unlock
      const gemText = this.towerSelectorGemTexts.get(type);
      if (gemText) {
        gemText.setVisible(needsResearch);
        if (needsResearch) {
          gemText.setColor(canAffordGems ? '#aa88ff' : '#ff4444');
        }
      }
      const gemIcon = this.towerSelectorGemIcons.get(type);
      if (gemIcon) {
        gemIcon.setVisible(needsResearch);
      }
      const unlockLabel = this.towerSelectorUnlockLabels.get(type);
      if (unlockLabel) {
        unlockLabel.setVisible(needsResearch);
      }
    });
  }

  // ============================================
  // UI Updates
  // ============================================

  private updateWaveDisplay(wave: number, total: number): void {
    this.waveText.setText(`Wave ${wave}/${total}`);
  }

  private updateGemsDisplay(gems: number): void {
    this.gemsText.setText(gems.toString());
  }

  private updateCoinsDisplay(coins: number): void {
    this.coinsText.setText(coins.toString());
  }

  private updateVillagersDisplay(villagers: number): void {
    this.villagersText.setText(villagers.toString());

    // Color based on danger level
    if (villagers <= 5) {
      this.villagersText.setColor('#ff0000');
    } else if (villagers <= 10) {
      this.villagersText.setColor('#ff8800');
    } else {
      this.villagersText.setColor('#ffffff');
    }
  }

  private updateMonstersDisplay(): void {
    const count = EntityManager.getCount('monster');
    this.monstersText.setText(count.toString());
  }

  // ============================================
  // Game End
  // ============================================

  private handleVictory(): void {
    if (this.isGameOver) return;
    this.isGameOver = true;

    GameState.setGameOver(true);
    AudioRouter.playVictory();
    console.log('[GameplayScene] VICTORY! Starting 10 second countdown...');

    // Check for perfect defense challenge
    if (SessionManager.getVillagers() === SessionManager.getMaxVillagers()) {
      console.log('[GameplayScene] Perfect defense! No villagers lost.');
    }

    // Start victory countdown - let players collect coins and sell towers
    this.startVictoryCountdown();
  }

  private startVictoryCountdown(): void {
    this.victoryCountdownActive = true;
    this.victoryCountdownTime = 10;

    // Create countdown display at top of screen
    this.victoryCountdownContainer = this.add.container(DESIGN_WIDTH / 2, 140);
    this.victoryCountdownContainer.setScrollFactor(0);
    this.victoryCountdownContainer.setDepth(RENDER_LAYERS.UI_SCREEN + 50);

    // Victory banner background
    const bannerBg = this.add.rectangle(0, 0, 500, 90, 0x228822, 0.9);
    bannerBg.setStrokeStyle(3, 0x44ff44);
    this.victoryCountdownContainer.add(bannerBg);

    // Victory text
    const victoryText = this.add.text(0, -25, '🎉 VICTORY! 🎉', {
      fontSize: '32px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    victoryText.setOrigin(0.5);
    this.victoryCountdownContainer.add(victoryText);

    // Countdown text
    this.victoryCountdownText = this.add.text(0, 15, 'Collect coins & sell towers! 10s', {
      fontSize: '18px',
      color: '#ffff88',
    });
    this.victoryCountdownText.setOrigin(0.5);
    this.victoryCountdownContainer.add(this.victoryCountdownText);

    // Add pulse effect to the banner
    this.tweens.add({
      targets: this.victoryCountdownContainer,
      scaleX: 1.02,
      scaleY: 1.02,
      duration: 500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private updateVictoryCountdown(delta: number): void {
    if (!this.victoryCountdownActive) return;

    this.victoryCountdownTime -= delta / 1000;

    if (this.victoryCountdownText) {
      const seconds = Math.ceil(this.victoryCountdownTime);
      if (seconds > 0) {
        this.victoryCountdownText.setText(`Collect coins & sell towers! ${seconds}s`);
        
        // Flash red when low on time
        if (seconds <= 3) {
          this.victoryCountdownText.setColor(seconds % 2 === 0 ? '#ff8888' : '#ffff88');
        }
      } else {
        this.victoryCountdownText.setText('Time\'s up!');
      }
    }

    // Countdown finished - go to results
    if (this.victoryCountdownTime <= 0) {
      this.victoryCountdownActive = false;
      this.stopMusic();

      // Clean up countdown UI
      if (this.victoryCountdownContainer) {
        this.tweens.killTweensOf(this.victoryCountdownContainer);
        this.victoryCountdownContainer.destroy();
        this.victoryCountdownContainer = null;
        this.victoryCountdownText = null;
      }

      // Transition to results
      this.time.delayedCall(500, () => {
        const payload = this.buildResultsPayload();
        SceneRouter.goToResults(SCENES.GAMEPLAY, payload);
      });
    }
  }

  private handleDefeat(): void {
    if (this.isGameOver) return;
    this.isGameOver = true;

    // Stop music immediately
    this.stopMusic();
    AudioRouter.playDefeat();

    GameState.setGameOver(false);
    console.log('[GameplayScene] DEFEAT');

    this.time.delayedCall(1500, () => {
      const payload = this.buildResultsPayload();
      SceneRouter.goToResults(SCENES.GAMEPLAY, payload);
    });
  }

  private buildResultsPayload() {
    const session = GameState.getSession();
    return {
      scenarioId: this.scenarioId,
      victory: WaveSpawner.isVictory(),
      wavesCompleted: WaveSpawner.getCurrentWave(),
      totalWaves: WaveSpawner.getTotalWaves(),
      monstersKilled: session.monstersKilled,
      monsterDamageDealt: session.monsterDamageDealt,
      towersBuilt: session.towersBuilt,
      villagersLost: SessionManager.getMaxVillagers() - SessionManager.getVillagers(),
      villagersRemaining: SessionManager.getVillagers(),
      maxVillagers: SessionManager.getMaxVillagers(),
      coinsRemaining: SessionManager.getCoins(),
      gemsRemaining: SessionManager.getGems(),
      timeElapsed: session.elapsedTime,
      score: 0, // Calculated by ProgressionSystem
      waveSkipBonuses: session.waveSkipBonuses,
      playerCount: GameState.getPlayerCount(),
    };
  }

  // ============================================
  // Pause Menu
  // ============================================

  private togglePause(): void {
    this.isPaused = !this.isPaused;
    GameState.setPaused(this.isPaused);

    if (this.isPaused) {
      this.showPauseMenu();
    } else {
      this.hidePauseMenu();
    }
  }

  private showPauseMenu(): void {
    InputRouter.setMenuMode(true); // Larger deadzone for pause menu navigation
    this.pauseOverlay = this.add.rectangle(
      DESIGN_WIDTH / 2,
      DESIGN_HEIGHT / 2,
      DESIGN_WIDTH,
      DESIGN_HEIGHT,
      COLORS.VOLCANIC_CHARCOAL,
      0.85
    );
    this.pauseOverlay.setScrollFactor(0);
    this.pauseOverlay.setDepth(RENDER_LAYERS.UI_SCREEN + 100);

    // Panel sized to fit sliders + all buttons (Resume, Restart, Quit, Controls)
    this.pausePanel = new UIPanel(this, {
      x: DESIGN_WIDTH / 2,
      y: DESIGN_HEIGHT / 2,
      width: 500,
      height: 600,
      title: 'PAUSED',
    });
    this.pausePanel.setScrollFactor(0);
    this.pausePanel.setDepth(RENDER_LAYERS.UI_SCREEN + 101);

    const settings = SettingsState.get();
    const centerX = DESIGN_WIDTH / 2;
    let yPos = DESIGN_HEIGHT / 2 - 140;

    // Volume sliders
    const masterSlider = new UISlider(this, {
      x: centerX,
      y: yPos,
      label: 'Master Volume',
      value: settings.masterVolume,
      onChange: (value) => {
        SettingsState.set('masterVolume', value);
        this.updateMusicVolume();
      },
    });
    masterSlider.setScrollFactor(0);
    masterSlider.setDepth(RENDER_LAYERS.UI_SCREEN + 102);
    yPos += 55;

    const musicSlider = new UISlider(this, {
      x: centerX,
      y: yPos,
      label: 'Music Volume',
      value: settings.musicVolume,
      onChange: (value) => {
        SettingsState.set('musicVolume', value);
        this.updateMusicVolume();
      },
    });
    musicSlider.setScrollFactor(0);
    musicSlider.setDepth(RENDER_LAYERS.UI_SCREEN + 102);
    yPos += 55;

    const sfxSlider = new UISlider(this, {
      x: centerX,
      y: yPos,
      label: 'SFX Volume',
      value: settings.sfxVolume,
      onChange: (value) => SettingsState.set('sfxVolume', value),
    });
    sfxSlider.setScrollFactor(0);
    sfxSlider.setDepth(RENDER_LAYERS.UI_SCREEN + 102);
    yPos += 70;

    this.pauseSliders = [masterSlider, musicSlider, sfxSlider];

    // Buttons
    this.pauseButtons = [
      new UIButton(this, {
        x: centerX,
        y: yPos,
        text: 'Resume',
        onClick: () => this.togglePause(),
      }),
      new UIButton(this, {
        x: centerX,
        y: yPos + 60,
        text: 'Restart',
        onClick: () => this.restart(),
      }),
      new UIButton(this, {
        x: centerX,
        y: yPos + 120,
        text: 'Quit',
        onClick: () => this.quitToMap(),
      }),
      new UIButton(this, {
        x: centerX,
        y: yPos + 180,
        text: 'Controls',
        onClick: () => this.showPauseControls(),
      }),
    ];

    this.pauseButtons.forEach((btn) => {
      btn.setScrollFactor(0);
      btn.setDepth(RENDER_LAYERS.UI_SCREEN + 102);
    });

    // Build combined elements array for navigation (sliders first, then buttons)
    this.pauseElements = [...this.pauseSliders, ...this.pauseButtons];

    // Default selection: Resume (index 3) so user can go down to Restart or up to sound sliders
    this.pauseSelectedIndex = this.pauseSliders.length;
    this.updatePauseSelection();
  }

  private updateMusicVolume(): void {
    if (this.bgMusic && 'setVolume' in this.bgMusic) {
      (this.bgMusic as Phaser.Sound.WebAudioSound).setVolume(SettingsState.getMusicVolume());
    }
  }

  private updatePauseSelection(): void {
    this.pauseElements.forEach((el, i) => {
      el.setSelected(i === this.pauseSelectedIndex);
    });
  }

  private showPauseControls(): void {
    this.pauseControlsVisible = true;
    const centerX = DESIGN_WIDTH / 2;
    const centerY = DESIGN_HEIGHT / 2;

    const lines: string[] = [
      '——— GAMEPAD ———',
      'Left stick / D-pad — Move',
      'A (Cross) — Interact / Build / Upgrade tower',
      'B (Circle) — Cancel',
      'X (Square) — Cycle tower (prev)',
      'Y (Triangle) — Sell tower',
      'L bumper — Cycle tower (prev)',
      'R bumper — Next wave',
      'ZL (L trigger) — Cycle tower (prev)',
      'ZR (R trigger) — Cycle tower (next)',
      'Start — Start',
      'Back/Select — Pause',
      '',
      '——— KEYBOARD (P1) ———',
      'W A S D — Move',
      'E / Space — Interact',
      'Q / Shift — Cancel',
      'Esc — Pause',
      'R — Cycle tower (next)',
      'F — Cycle tower (prev)',
      'Enter — Start',
      'G — Dance',
      'T — Upgrade tower',
      'X — Sell tower',
      'N — Next wave',
      'Mouse left — Interact',
      'Mouse right — Cancel',
      '',
      '——— KEYBOARD (P2) ———',
      'Arrow keys — Move',
      'Numpad 0 / Enter — Interact',
      'Shift (right) — Cancel',
      'Backspace — Pause',
      'Numpad + — Cycle next',
      'Numpad - — Cycle prev',
      'Numpad . — Dance',
      'Numpad * — Upgrade',
    ];
    const lineHeight = 18;
    const titleBarHeight = 50;
    const backButtonArea = 70;
    const contentHeight = lines.length * lineHeight + titleBarHeight + backButtonArea;

    this.pauseControlsPanel = new UIPanel(this, {
      x: centerX,
      y: centerY,
      width: 520,
      height: contentHeight,
      title: 'CONTROLS',
    });
    this.pauseControlsPanel.setScrollFactor(0);
    this.pauseControlsPanel.setDepth(RENDER_LAYERS.UI_SCREEN + 103);

    const startY = -contentHeight / 2 + titleBarHeight;
    lines.forEach((line, i) => {
      const text = this.add.text(0, startY + i * lineHeight, line, {
        fontSize: '14px',
        color: line.startsWith('———') ? '#aaaaaa' : '#ffffff',
      });
      text.setOrigin(0.5, 0);
      this.pauseControlsPanel!.add(text);
    });

    const backButtonY = centerY + contentHeight / 2 - backButtonArea / 2 - 10;
    this.pauseControlsBackButton = new UIButton(this, {
      x: centerX,
      y: backButtonY,
      text: 'Back',
      onClick: () => this.hidePauseControls(),
    });
    this.pauseControlsBackButton.setScrollFactor(0);
    this.pauseControlsBackButton.setDepth(RENDER_LAYERS.UI_SCREEN + 104);
    this.pauseControlsBackButton.setSelected(true); // Focus Back so A/Enter activates it
  }

  private hidePauseControls(): void {
    this.pauseControlsVisible = false;
    this.pauseControlsPanel?.destroy();
    this.pauseControlsPanel = null;
    this.pauseControlsBackButton?.destroy();
    this.pauseControlsBackButton = null;
  }

  private hideTutorialOverlay(): void {
    this.tutorialOverlayContainer?.destroy();
    this.tutorialOverlayContainer = null;
  }

  private advanceTutorial(step: number): void {
    if (step === 0) {
      this.showTutorialStep(1);
      return;
    }
    if (step === 1) {
      this.hideTutorialOverlay();
      this.tutorialStep = 2;
      return;
    }
    if (step === 3) {
      this.hideTutorialOverlay();
      this.tutorialStep = 4;
      WaveSpawner.manualStartWave();
    }
  }

  private showTutorialStep(step: number): void {
    this.hideTutorialOverlay();
    this.tutorialStep = step;

    const centerX = DESIGN_WIDTH / 2;
    const centerY = DESIGN_HEIGHT / 2;
    const panelWidth = 700;
    const padding = 40;
    const textWidth = panelWidth - padding * 2;
    const fontSize = '20px';

    let bodyText = '';
    if (step === 0) {
      bodyText = 'Monsters are here and attacking your village. You need to build defense towers on trees in order to stop them and save your people.';
    } else if (step === 1) {
      bodyText = 'CONTROLS\n\n' +
        '• Move: WASD (keyboard) or left stick / D-pad (gamepad).\n\n' +
        '• Build a tower: Move onto a tree, then press E or Space (keyboard) or A (gamepad) to build. Building costs coins — see the bottom of the screen.\n\n' +
        'Build one tower on any tree when you\'re ready.';
    } else if (step === 3) {
      bodyText = 'UPGRADING TOWERS\n\n' +
        '• Stand on your tower to dance and upgrade it over time (no button needed — just stand there). Or upgrade instantly with gems (T on keyboard, or gamepad).\n\n' +
        '• At the bottom, switch which tower type to place: R / F (keyboard) or ZL / ZR (gamepad) to cycle. Then build on a tree as before.\n\n' +
        'When you\'re ready, we\'ll start one wave with a single monster. Defeat it to win!';
    }

    // Measure wrapped text height so panel can be size-responsive
    const measureText = this.add.text(0, 0, bodyText, {
      fontSize,
      color: '#ffffff',
      align: 'center',
      wordWrap: { width: textWidth },
      lineSpacing: 8,
    });
    measureText.setOrigin(0.5, 0);
    const contentHeight = measureText.height;
    measureText.destroy();

    const topPadding = 40;
    const gapBetweenTextAndButton = 24;
    const buttonHeight = 44;
    const bottomPadding = 50;
    const panelHeight = Math.min(
      DESIGN_HEIGHT - 80,
      topPadding + contentHeight + gapBetweenTextAndButton + buttonHeight + bottomPadding
    );

    const container = this.add.container(0, 0);
    container.setDepth(RENDER_LAYERS.UI_SCREEN + 200);

    const dimBg = this.add.rectangle(centerX, centerY, DESIGN_WIDTH, DESIGN_HEIGHT, 0x000000, 0.7);
    dimBg.setScrollFactor(0);
    container.add(dimBg);

    const panelBg = this.add.rectangle(centerX, centerY, panelWidth, panelHeight, COLORS.BG_DARK, 0.98);
    panelBg.setStrokeStyle(3, COLORS.PRIMARY);
    panelBg.setScrollFactor(0);
    container.add(panelBg);

    const body = this.add.text(centerX, centerY - panelHeight / 2 + topPadding, bodyText, {
      fontSize,
      color: '#ffffff',
      align: 'center',
      wordWrap: { width: textWidth },
      lineSpacing: 8,
    });
    body.setOrigin(0.5, 0);
    body.setScrollFactor(0);
    container.add(body);

    const continueBtnY = centerY + panelHeight / 2 - bottomPadding - buttonHeight / 2;
    const continueBtn = new UIButton(this, {
      x: centerX,
      y: continueBtnY,
      text: 'Continue',
      onClick: () => this.advanceTutorial(step),
    });
    continueBtn.setScrollFactor(0);
    continueBtn.setDepth(RENDER_LAYERS.UI_SCREEN + 201);
    continueBtn.setSelected(true); // So keyboard/gamepad can activate with Space, Enter, or A
    container.add(continueBtn);

    this.tutorialOverlayContainer = container;
  }

  private handlePauseMenuInput(): void {
    // When viewing controls, Back button is focused: INTERACT/START activates it, CANCEL closes
    if (this.pauseControlsVisible) {
      if (InputRouter.anyPlayerJustPressed('INTERACT') || InputRouter.anyPlayerJustPressed('START')) {
        this.pauseControlsBackButton?.click();
      }
      if (InputRouter.anyPlayerJustPressed('CANCEL')) {
        this.hidePauseControls();
      }
      return;
    }

    const elementCount = this.pauseElements.length;
    if (elementCount === 0) return;

    // Navigate up/down with D-pad or left stick
    if (InputRouter.anyPlayerJustPressed('MOVE_UP')) {
      this.pauseSelectedIndex = (this.pauseSelectedIndex - 1 + elementCount) % elementCount;
      this.updatePauseSelection();
    }
    if (InputRouter.anyPlayerJustPressed('MOVE_DOWN')) {
      this.pauseSelectedIndex = (this.pauseSelectedIndex + 1) % elementCount;
      this.updatePauseSelection();
    }

    // Left/Right adjusts sliders
    const selectedElement = this.pauseElements[this.pauseSelectedIndex];
    if (selectedElement instanceof UISlider) {
      if (InputRouter.anyPlayerJustPressed('MOVE_LEFT')) {
        selectedElement.decrement(0.05);
      }
      if (InputRouter.anyPlayerJustPressed('MOVE_RIGHT')) {
        selectedElement.increment(0.05);
      }
    }

    // Select with A button (INTERACT) - only for buttons
    if (InputRouter.anyPlayerJustPressed('INTERACT')) {
      if (selectedElement instanceof UIButton) {
        selectedElement.click();
      }
    }

    // Also allow B button to resume (CANCEL)
    if (InputRouter.anyPlayerJustPressed('CANCEL')) {
      this.togglePause();
    }
  }

  private hidePauseMenu(): void {
    InputRouter.setMenuMode(false); // Back to gameplay deadzone
    this.hidePauseControls();
    this.pauseOverlay?.destroy();
    this.pauseOverlay = null;
    this.pausePanel?.destroy();
    this.pausePanel = null;
    this.pauseSliders.forEach((slider) => slider.destroy());
    this.pauseSliders = [];
    this.pauseButtons.forEach((btn) => btn.destroy());
    this.pauseButtons = [];
    this.pauseElements = [];
  }

  private restart(): void {
    this.hidePauseMenu();
    this.scene.restart({ scenarioId: this.scenarioId });
  }

  private quitToMap(): void {
    this.stopMusic();
    AudioRouter.stopAllConstruction();
    this.sound.stopAll();
    this.hidePauseMenu();
    SceneRouter.goToMap(SCENES.GAMEPLAY);
  }

  // ============================================
  // Update Loop
  // ============================================

  update(_time: number, delta: number): void {
    // Update input system
    InputRouter.update();

    // Update tower-placement hover so INTERACT/click uses current slot (avoids needing to click twice)
    TowerPlacementSystem.updateHoverState();

    // Handle pause toggle
    if (InputRouter.anyPlayerJustPressed('PAUSE')) {
      this.togglePause();
      InputRouter.lateUpdate();
      return;
    }

    // Handle pause menu navigation when paused
    if (this.isPaused && this.pauseButtons.length > 0) {
      this.handlePauseMenuInput();
      InputRouter.lateUpdate();
      return;
    }

    // When tutorial overlay is visible, INTERACT/START activates Continue
    if (this.tutorialOverlayContainer) {
      if (InputRouter.anyPlayerJustPressed('INTERACT') || InputRouter.anyPlayerJustPressed('START')) {
        this.advanceTutorial(this.tutorialStep);
      }
      InputRouter.lateUpdate();
      return;
    }

    // Allow updates during victory countdown so players can still play
    if (this.isPaused) return;
    if (this.isGameOver && !this.victoryCountdownActive) return;

    // Update victory countdown if active
    if (this.victoryCountdownActive) {
      this.updateVictoryCountdown(delta);
    }

    GameState.updateElapsedTime();

    // A button / INTERACT: build tower or upgrade tower (if standing on a built tower)
    ALL_PLAYER_IDS.forEach(playerId => {
      if (InputRouter.wasActionJustPressed(playerId, 'INTERACT')) {
        const interaction = TowerInteractionSystem.getInteraction(playerId);
        if (interaction && !interaction.tower.isBuilding()) {
          TowerInteractionSystem.tryInstantUpgrade(playerId);
        } else {
          TowerPlacementSystem.tryPlaceTower(playerId);
        }
      }
    });

    // Handle tower cycling - per player (ZL/ZR triggers, or keyboard)
    ALL_PLAYER_IDS.forEach(playerId => {
      if (InputRouter.wasActionJustPressed(playerId, 'CYCLE_NEXT')) {
        this.cycleTowerType(1, playerId);
      }
      if (InputRouter.wasActionJustPressed(playerId, 'CYCLE_PREV')) {
        this.cycleTowerType(-1, playerId);
      }
    });

    // Handle number key tower selection (for P1 only - keyboard)
    for (let i = 0; i < Math.min(12, this.availableTowers.length); i++) {
      if (this.input.keyboard?.checkDown(this.input.keyboard.addKey(49 + i), 0)) {
        if (this.availableTowers[i]) {
          // Use same handler as clicking (handles research too)
          this.handleTowerSelectorClick(this.availableTowers[i], 'P1');
        }
      }
    }

    // Next wave: right bumper or N key
    if (InputRouter.anyPlayerJustPressed('NEXT_WAVE')) {
      this.forceNextWave();
    }

    // Handle dancing (D key to toggle dance on nearest tower)
    this.handleDancing();

    // Update all systems
    WaveSpawner.update(delta);
    TowerPlacementSystem.update();
    DropSystem.update(delta);
    // Build tree data with positions and occupied status
    const treeData = new Map<string, { x: number; y: number; occupied: boolean }>();
    this.treeSlots.forEach((slot, id) => {
      treeData.set(id, { x: slot.x, y: slot.y, occupied: slot.isOccupied() });
    });
    DropSystem.updateTreeDrops(treeData);
    TowerInteractionSystem.update(delta);
    EntityManager.update(delta);
    DebugSystem.update(delta);

    // Check player-monster collisions for coin spill
    this.checkPlayerMonsterCollisions();

    // Constrain players
    this.constrainPlayers();

    // Update UI
    this.updateMonstersDisplay();
    this.updateTowerSelector(); // Update affordability colors
    this.bossHealthBar?.update();
    this.wavePreviewUI?.update();

    // End of frame - prepare input for next frame
    InputRouter.lateUpdate();
  }

  private handleDancing(): void {
    // D (or DANCE) toggles: if already dancing on tower, stop; else try start on nearest tower
    const pressed = InputRouter.anyPlayerJustPressed('DANCE');
    if (pressed === null) return;

    const playerId = pressed;
    if (DanceSystem.isDancing(playerId)) {
      DanceSystem.stopDance(playerId);
    } else {
      DanceSystem.tryStartDance(playerId);
    }
  }

  private cycleTowerType(direction: number, playerId: PlayerId): void {
    const currentSelection = this.playerTowerSelections.get(playerId) || 'arrow';
    const currentIndex = this.availableTowers.indexOf(currentSelection);
    
    // Allow cycling to ALL towers including unresearched ones
    const newIndex = (currentIndex + direction + this.availableTowers.length) % this.availableTowers.length;
    const newTower = this.availableTowers[newIndex];
    
    this.playerTowerSelections.set(playerId, newTower);
    TowerPlacementSystem.setSelectedTowerType(newTower, playerId);
    this.updateTowerSelector();
  }

  private constrainPlayers(): void {
    if (!this.level) return;

    const { gridConfig } = this.level;
    const padding = 32;
    const minX = padding;
    const minY = padding;
    const maxX = gridConfig.mapWidthTiles * gridConfig.tileWidth - padding;
    const maxY = gridConfig.mapHeightTiles * gridConfig.tileHeight - padding;

    const players = EntityManager.getByType<Player>('player');
    players.forEach(player => {
      player.constrainToBounds(minX, minY, maxX, maxY);
    });
  }

  /**
   * Check for player-monster collisions and trigger coin spill
   */
  private checkPlayerMonsterCollisions(): void {
    const players = EntityManager.getByType<Player>('player');
    const monsters = EntityManager.getByType<Monster>('monster');

    const collisionRadius = 20; // Player collision radius

    for (const player of players) {
      // Skip if player is in grace period
      if (DropSystem.isPlayerInGracePeriod(player.getPlayerId())) continue;
      // Dancing on a tower: cannot be hit by monsters or lose coins (we never call handlePlayerHit)
      if (player.isUpgradeDancing()) continue;

      for (const monster of monsters) {
        if (monster.destroyed) continue;
        // Only ground monsters can hit the player; flying monsters pass through
        if (monster.getMovementType() === 'air') continue;

        const dist = player.distanceTo(monster);
        if (dist < collisionRadius + 10) { // 10 = approximate monster radius
          // Player hit by monster!
          const didSpill = DropSystem.handlePlayerHit(
            player.getPlayerId(),
            player.x,
            player.y
          );

          if (didSpill) {
            AudioRouter.playSfx('sfx-hit-bad');
            player.playHitBounce();
            player.flashRed(1000);
            player.setInvincible(true);
            InputRouter.vibratePlayerGamepad(player.getPlayerId(), 200);
          }

          break; // Only one collision per player per frame
        }
      }

      // Check if grace period ended - stop blinking
      if (player.isInvincible() && !DropSystem.isPlayerInGracePeriod(player.getPlayerId())) {
        player.setInvincible(false);
      }
    }
  }

  private flashPlayerHit(player: Player): void {
    // Visual feedback - flash red briefly on hit
    const container = player.getContainer();
    
    // Quick red flash
    this.tweens.add({
      targets: container,
      alpha: 0.2,
      duration: 100,
      yoyo: true,
      repeat: 2,
    });
  }

  /**
   * Force start the next wave (can be called mid-wave to stack waves)
   * Awards +100 bonus if skipped >5 seconds before wave would auto-start
   */
  private forceNextWave(): void {
    // Check time until next wave BEFORE calling force (which resets it)
    const timeUntilNext = WaveSpawner.getTimeUntilNextWave();
    
    const success = WaveSpawner.forceNextWave();
    if (success) {
      // Award skip bonus if we skipped >5 seconds early
      if (timeUntilNext > 5) {
        GameState.addWaveSkipBonus();
        this.showWaveSkipFeedback(true); // Show with bonus indicator
      } else {
        this.showWaveSkipFeedback(false);
      }
    }
  }

  /** Show how much wave interest (5% coin bonus) the player earned. */
  private showWaveInterestFeedback(amount: number): void {
    const text = this.add.text(DESIGN_WIDTH / 2, 95, `Wave interest: +${amount}`, {
      fontSize: '18px',
      color: '#88ff88',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 4,
    });
    text.setOrigin(0.5);
    text.setScrollFactor(0);
    text.setDepth(RENDER_LAYERS.UI_SCREEN + 50);

    this.tweens.add({
      targets: text,
      y: text.y - 25,
      alpha: 0,
      duration: 2000,
      ease: 'Power2',
      onComplete: () => text.destroy(),
    });
  }

  private showWaveSkipFeedback(gotBonus: boolean = false): void {
    const text = this.add.text(DESIGN_WIDTH / 2, DESIGN_HEIGHT / 2 - 75, 'NEXT WAVE!', {
      fontSize: '36px',
      color: '#ffff00',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 6,
    });
    text.setOrigin(0.5);
    text.setScrollFactor(0);
    text.setDepth(RENDER_LAYERS.UI_SCREEN + 50);

    this.tweens.add({
      targets: text,
      y: text.y - 45,
      alpha: 0,
      scale: 1.5,
      duration: 800,
      onComplete: () => text.destroy(),
    });

    // Show bonus indicator if earned
    if (gotBonus) {
      const bonusText = this.add.text(DESIGN_WIDTH / 2, DESIGN_HEIGHT / 2 - 35, '+100 SKIP BONUS!', {
        fontSize: '18px',
        color: '#44ff44',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 4,
      });
      bonusText.setOrigin(0.5);
      bonusText.setScrollFactor(0);
      bonusText.setDepth(RENDER_LAYERS.UI_SCREEN + 50);

      this.tweens.add({
        targets: bonusText,
        y: bonusText.y - 30,
        alpha: 0,
        duration: 1200,
        onComplete: () => bonusText.destroy(),
      });
    }
  }

  // ============================================
  // Experiment Mode - Spawn Panel
  // ============================================

  private createExperimentSpawnPanel(): void {
    // IMPORTANT: Don't use container with setScrollFactor(0) for interactive elements
    // Instead, add each element directly to the scene with setScrollFactor(0)
    // This fixes the click position offset bug when camera has scrolled
    
    this.experimentUIElements = [];
    const depth = RENDER_LAYERS.UI_SCREEN + 10;
    
    // Panel position (absolute screen coordinates)
    const panelX = DESIGN_WIDTH - 200;  // Center of panel
    const panelY = 265;  // Center of panel

    // Helper to add UI element with proper settings
    const addUI = <T extends Phaser.GameObjects.GameObject>(obj: T): T => {
      (obj as unknown as { setScrollFactor: (v: number) => void; setDepth: (v: number) => void }).setScrollFactor(0);
      (obj as unknown as { setScrollFactor: (v: number) => void; setDepth: (v: number) => void }).setDepth(depth);
      this.experimentUIElements.push(obj);
      return obj;
    };

    // Panel background
    const panelBg = addUI(this.add.rectangle(panelX, panelY, 360, 550, COLORS.BG_DARK, 0.95));
    panelBg.setStrokeStyle(2, COLORS.ACCENT);

    // Title
    const title = addUI(this.add.text(panelX, panelY - 235, '🧪 EXPERIMENT MODE', {
      fontSize: '18px',
      color: '#D8A43A', // Tiki Gold - currency
      fontStyle: 'bold',
    }));
    title.setOrigin(0.5, 0);

    // Biome selector (Forest / Bog / Ruins / Volcano)
    addUI(this.add.text(panelX - 160, panelY - 200, 'BIOME:', {
      fontSize: '12px',
      color: '#aaaaaa',
    }));
    const currentBiome = GameState.getSelectedBiome();
    const forestBiomeBtn = addUI(this.add.rectangle(panelX - 130, panelY - 175, 48, 28, currentBiome === 'forest' ? 0x228b22 : 0x334433));
    forestBiomeBtn.setStrokeStyle(1, 0x448844);
    forestBiomeBtn.setInteractive({ useHandCursor: true });
    addUI(this.add.text(panelX - 130, panelY - 175, 'Forest', {
      fontSize: '10px',
      color: '#ffffff',
    })).setOrigin(0.5);
    const bogBiomeBtn = addUI(this.add.rectangle(panelX - 78, panelY - 175, 48, 28, currentBiome === 'bog' ? 0x556b2f : 0x334433));
    bogBiomeBtn.setStrokeStyle(1, 0x668855);
    bogBiomeBtn.setInteractive({ useHandCursor: true });
    addUI(this.add.text(panelX - 78, panelY - 175, 'Bog', {
      fontSize: '10px',
      color: '#ffffff',
    })).setOrigin(0.5);
    const ruinsBiomeBtn = addUI(this.add.rectangle(panelX - 26, panelY - 175, 48, 28, currentBiome === 'ruins' ? 0x6b5d4f : 0x334433));
    ruinsBiomeBtn.setStrokeStyle(1, 0x8a7a6a);
    ruinsBiomeBtn.setInteractive({ useHandCursor: true });
    addUI(this.add.text(panelX - 26, panelY - 175, 'Ruins', {
      fontSize: '10px',
      color: '#ffffff',
    })).setOrigin(0.5);
    const volcanoBiomeBtn = addUI(this.add.rectangle(panelX + 26, panelY - 175, 48, 28, currentBiome === 'volcano' ? 0x8b3510 : 0x334433));
    volcanoBiomeBtn.setStrokeStyle(1, 0xaa5522);
    volcanoBiomeBtn.setInteractive({ useHandCursor: true });
    addUI(this.add.text(panelX + 26, panelY - 175, 'Volcano', {
      fontSize: '9px',
      color: '#ffffff',
    })).setOrigin(0.5);
    const setAllBiomeBtnsInactive = () => {
      forestBiomeBtn.setFillStyle(0x334433);
      bogBiomeBtn.setFillStyle(0x334433);
      ruinsBiomeBtn.setFillStyle(0x334433);
      volcanoBiomeBtn.setFillStyle(0x334433);
    };
    forestBiomeBtn.on('pointerdown', () => {
      this.applyExperimentBiome('forest');
      setAllBiomeBtnsInactive();
      forestBiomeBtn.setFillStyle(0x228b22);
    });
    bogBiomeBtn.on('pointerdown', () => {
      this.applyExperimentBiome('bog');
      setAllBiomeBtnsInactive();
      bogBiomeBtn.setFillStyle(0x556b2f);
    });
    ruinsBiomeBtn.on('pointerdown', () => {
      this.applyExperimentBiome('ruins');
      setAllBiomeBtnsInactive();
      ruinsBiomeBtn.setFillStyle(0x6b5d4f);
    });
    volcanoBiomeBtn.on('pointerdown', () => {
      this.applyExperimentBiome('volcano');
      setAllBiomeBtnsInactive();
      volcanoBiomeBtn.setFillStyle(0x8b3510);
    });

    // Monster section label
    addUI(this.add.text(panelX - 160, panelY - 155, 'MONSTERS:', {
      fontSize: '14px',
      color: '#ffffff',
      fontStyle: 'bold',
    }));

    // Monster spawn buttons (in grid)
    const monsterTypes: Array<{ type: string; label: string; color: string }> = [
      { type: 'crawler', label: 'Crawler', color: '#88ff88' },
      { type: 'runner', label: 'Runner', color: '#ffff88' },
      { type: 'brute', label: 'Brute', color: '#ff8888' },
      { type: 'shielded', label: 'Shielded', color: '#8888ff' },
      { type: 'bat', label: 'Bat', color: '#cc88ff' },
      { type: 'flyer', label: 'Flyer', color: '#88ffff' },
      { type: 'swarm', label: 'Swarm', color: '#ffcc88' },
      { type: 'juggernaut', label: 'Jugger', color: '#ff4444' },
      { type: 'phased', label: 'Phased', color: '#44ffff' },
    ];

    const btnWidth = 80;
    const btnHeight = 30;
    const cols = 4;
    const startX = panelX - 155;
    const startY = panelY - 125;

    monsterTypes.forEach((monster, index) => {
      const col = index % cols;
      const row = Math.floor(index / cols);
      const x = startX + col * (btnWidth + 5) + btnWidth / 2;
      const y = startY + row * (btnHeight + 5);

      const btn = addUI(this.add.rectangle(x, y, btnWidth, btnHeight, COLORS.BG_MEDIUM));
      btn.setStrokeStyle(1, COLORS.DEEP_AMBER);
      btn.setInteractive({ useHandCursor: true });
      
      const label = addUI(this.add.text(x, y, monster.label, {
        fontSize: '11px',
        color: monster.color,
      }));
      label.setOrigin(0.5);

      btn.on('pointerover', () => btn.setStrokeStyle(2, COLORS.PRIMARY));
      btn.on('pointerout', () => btn.setStrokeStyle(1, COLORS.DEEP_AMBER));
      btn.on('pointerdown', () => this.spawnExperimentMonster(monster.type));
    });

    // Boss section label
    addUI(this.add.text(panelX - 160, panelY - 5, 'BOSSES:', {
      fontSize: '14px',
      color: '#ffffff',
      fontStyle: 'bold',
    }));

    // Boss spawn buttons
    const bossTypes: Array<{ type: string; label: string; color: string }> = [
      { type: 'brute_boss', label: 'Brute Boss', color: '#8b4513' },
      { type: 'bronze_idol', label: 'Bronze Idol', color: '#cd7f32' },
      { type: 'earth_tower', label: 'Earth Tower', color: '#8b4513' },
      { type: 'snail_beast', label: 'Snail Beast', color: '#9932cc' },
      { type: 'giant_bug', label: 'Giant Bug', color: '#9400d3' },
    ];

    bossTypes.forEach((boss, index) => {
      const x = panelX - 75 + (index % 2) * 160;
      const y = panelY + 25 + Math.floor(index / 2) * 40;

      const btn = addUI(this.add.rectangle(x, y, 140, 32, 0x553333));
      btn.setStrokeStyle(1, 0xaa6666);
      btn.setInteractive({ useHandCursor: true });
      
      const label = addUI(this.add.text(x, y, boss.label, {
        fontSize: '12px',
        color: boss.color,
        fontStyle: 'bold',
      }));
      label.setOrigin(0.5);

      btn.on('pointerover', () => btn.setStrokeStyle(2, 0xff4444));
      btn.on('pointerout', () => btn.setStrokeStyle(1, 0xaa6666));
      btn.on('pointerdown', () => this.spawnExperimentBoss(boss.type));
    });

    // Wave size controls
    addUI(this.add.text(panelX - 160, panelY + 120, 'WAVE SIZE:', {
      fontSize: '12px',
      color: '#aaaaaa',
    }));

    const waveSizeButtons: Phaser.GameObjects.Rectangle[] = [];
    const waveSizes: Array<{ label: string; hp: number }> = [
      { label: 'Small', hp: 250 },
      { label: 'Med', hp: 500 },
      { label: 'Large', hp: 1000 },
      { label: 'Huge', hp: 2000 },
    ];
    
    waveSizes.forEach((size, index) => {
      const x = panelX - 130 + index * 70;
      const y = panelY + 145;

      const btn = addUI(this.add.rectangle(x, y, 60, 28, size.hp === this.experimentWaveHP ? 0x446644 : 0x334433));
      btn.setStrokeStyle(1, 0x668866);
      btn.setInteractive({ useHandCursor: true });
      btn.setData('hp', size.hp);
      waveSizeButtons.push(btn);
      
      const label = addUI(this.add.text(x, y, size.label, {
        fontSize: '12px',
        color: '#ffffff',
      }));
      label.setOrigin(0.5);

      btn.on('pointerdown', () => {
        this.experimentWaveHP = size.hp;
        waveSizeButtons.forEach(b => {
          b.setFillStyle(b.getData('hp') === size.hp ? 0x446644 : 0x334433);
        });
      });
    });

    // Path selector section
    addUI(this.add.text(panelX - 160, panelY + 180, 'SPAWN PATH:', {
      fontSize: '12px',
      color: '#aaaaaa',
    }));

    const pathButtons: Phaser.GameObjects.Rectangle[] = [];
    const pathCount = this.level?.pathsWorld.length || 1;
    const maxPathButtons = Math.min(pathCount, 6);
    
    for (let i = 0; i < maxPathButtons; i++) {
      const pathId = this.level?.pathsWorld[i]?.id || `path-${i + 1}`;
      const x = panelX - 130 + i * 55;
      const y = panelY + 205;

      const btn = addUI(this.add.rectangle(x, y, 50, 28, i === this.experimentSelectedPathIndex ? 0x446688 : 0x334455));
      btn.setStrokeStyle(1, 0x6688aa);
      btn.setInteractive({ useHandCursor: true });
      btn.setData('pathIndex', i);
      pathButtons.push(btn);
      
      const label = addUI(this.add.text(x, y, `P${i + 1}`, {
        fontSize: '12px',
        color: '#88ccff',
        fontStyle: 'bold',
      }));
      label.setOrigin(0.5);

      btn.on('pointerdown', () => {
        this.experimentSelectedPathIndex = i;
        pathButtons.forEach(b => {
          b.setFillStyle(b.getData('pathIndex') === i ? 0x446688 : 0x334455);
        });
        console.log(`[Experiment] Selected path: ${pathId}`);
      });
    }

    // Path count info
    if (pathCount > 0) {
      const pathInfo = addUI(this.add.text(panelX + 160, panelY + 205, `(${pathCount} paths)`, {
        fontSize: '10px',
        color: '#666666',
      }));
      pathInfo.setOrigin(1, 0.5);
    }

    // Exit experiment button
    const exitBtn = addUI(this.add.rectangle(panelX, panelY + 255, 200, 40, 0x663333));
    exitBtn.setStrokeStyle(2, 0xff4444);
    exitBtn.setInteractive({ useHandCursor: true });
    
    const exitLabel = addUI(this.add.text(panelX, panelY + 255, 'EXIT EXPERIMENT', {
      fontSize: '14px',
      color: '#ff4444',
      fontStyle: 'bold',
    }));
    exitLabel.setOrigin(0.5);

    exitBtn.on('pointerover', () => exitBtn.setFillStyle(0x884444));
    exitBtn.on('pointerout', () => exitBtn.setFillStyle(0x663333));
    exitBtn.on('pointerdown', () => {
      GameState.endExperimentMode();
      this.stopMusic();
      AudioRouter.stopAllConstruction();
      this.sound.stopAll();
      SceneRouter.goToMap(SCENES.GAMEPLAY);
    });

    // Also create an empty container for backward compatibility (some code checks this)
    this.spawnPanelContainer = this.add.container(0, 0);
  }

  private experimentWaveHP: number = 500; // Default to medium wave
  private experimentSelectedPathIndex: number = 0; // Which path to spawn on

  // Monster HP lookup for wave calculation (must match Monster MONSTER_STATS)
  private static readonly MONSTER_HP: Record<string, number> = {
    crawler: 30,
    runner: 18,
    brute: 120,
    shielded: 60,
    bat: 14,
    flyer: 42,
    swarm: 6,
    juggernaut: 300,
    phased: 72,
  };

  private spawnExperimentMonster(monsterType: string): void {
    if (!this.level) return;

    // Get selected path from pathsWorld
    if (this.level.pathsWorld.length === 0) {
      console.error('[Experiment] No paths found in level!');
      return;
    }
    
    // Use selected path index, fallback to first path
    const pathIndex = Math.min(this.experimentSelectedPathIndex, this.level.pathsWorld.length - 1);
    const pathId = this.level.pathsWorld[pathIndex].id;

    // Calculate how many monsters to spawn based on wave HP total
    const monsterHP = GameplayScene.MONSTER_HP[monsterType] || 25;
    const spawnCount = Math.max(1, Math.ceil(this.experimentWaveHP / monsterHP));
    
    // Default 2 seconds between monsters in waves
    const spawnInterval = 2000;

    console.log(`[Experiment] Spawning wave of ${spawnCount} ${monsterType} (${this.experimentWaveHP} HP total)`);

    // Spawn monsters over time like a wave
    for (let i = 0; i < spawnCount; i++) {
      this.time.delayedCall(i * spawnInterval, () => {
        const routeId = PathingSystem.selectRoute(pathId);
        const startPos = PathingSystem.getPathStart(pathId, routeId);
        
        if (!startPos) {
          console.error(`[Experiment] Could not get start position for path ${pathId}`);
          return;
        }

        const monster = new Monster(this, {
          id: `exp-monster-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
          x: startPos.x,
          y: startPos.y,
          monsterType: monsterType as MonsterType,
          pathId,
          routeId,
        });

        monster.onReachedBase = () => {
          SessionManager.removeVillager(monster.getDamage());
          EntityManager.remove(monster.id);
        };

        monster.onDeath = (reward: number) => {
          SessionManager.addGems(reward);
          EntityManager.remove(monster.id);
        };

        EntityManager.add(monster);
      });
    }

    // Visual feedback
    const text = this.add.text(DESIGN_WIDTH / 2, DESIGN_HEIGHT / 2, 
      `Wave: ${spawnCount} ${monsterType}! (${this.experimentWaveHP} HP)`, {
      fontSize: '20px',
      color: '#88ff88',
      fontStyle: 'bold',
    });
    text.setOrigin(0.5);
    text.setScrollFactor(0);
    text.setDepth(RENDER_LAYERS.UI_SCREEN + 50);
    this.tweens.add({
      targets: text,
      alpha: 0,
      y: text.y - 30,
      duration: 1500,
      onComplete: () => text.destroy(),
    });
  }

  private spawnExperimentBoss(bossType: string): void {
    if (!this.level) return;

    // Get selected path from pathsWorld
    if (this.level.pathsWorld.length === 0) {
      console.error('[Experiment] No paths found for boss!');
      return;
    }
    
    // Use selected path index, fallback to first path
    const pathIndex = Math.min(this.experimentSelectedPathIndex, this.level.pathsWorld.length - 1);
    const pathId = this.level.pathsWorld[pathIndex].id;

    const routeId = PathingSystem.selectRoute(pathId);
    const startPos = PathingSystem.getPathStart(pathId, routeId);
    if (!startPos) return;

    const boss = new Boss(this, {
      id: `exp-boss-${Date.now()}`,
      x: startPos.x,
      y: startPos.y,
      bossType: bossType as BossType,
      pathId,
      routeId,
    });

    boss.onReachedBase = () => {
      // Boss eats villagers over time (handled by boss)
    };

    boss.onEatVillager = (damage) => {
      if (!DebugSystem.isGodMode()) {
        const remaining = SessionManager.removeVillager(damage);
        if (remaining <= 0) {
          WaveSpawner.triggerDefeat();
        }
      }
    };

    boss.onDeath = () => {
      SessionManager.addGems(50); // Boss reward
      this.bossHealthBar?.hide();
      EntityManager.remove(boss.id);
    };

    EntityManager.add(boss);
    this.bossHealthBar?.show(boss);

    // Visual feedback
    const text = this.add.text(DESIGN_WIDTH / 2, DESIGN_HEIGHT / 2, 
      `BOSS SPAWNED: ${bossType}!`, {
      fontSize: '24px',
      color: '#ff4444',
      fontStyle: 'bold',
    });
    text.setOrigin(0.5);
    text.setScrollFactor(0);
    text.setDepth(RENDER_LAYERS.UI_SCREEN + 50);
    this.tweens.add({
      targets: text,
      alpha: 0,
      scale: 1.5,
      duration: 1500,
      onComplete: () => text.destroy(),
    });
  }

  shutdown(): void {
    // Stop game music first so it never keeps playing after leaving
    this.stopMusic();
    // Stop all SFX (construction loops, tower fire, coin collect, etc.) so they don't keep playing
    AudioRouter.stopAllConstruction();
    this.sound.stopAll();
    // Remove visibility and blur listeners
    if (this.boundVisibilityHandler) {
      document.removeEventListener('visibilitychange', this.boundVisibilityHandler);
      this.boundVisibilityHandler = null;
    }
    if (this.boundBlurHandler) {
      window.removeEventListener('blur', this.boundBlurHandler);
      this.boundBlurHandler = null;
    }

    // Clean up experiment mode and clear global flag so next level (normal or not) loads correctly
    if (this.spawnPanelContainer) {
      this.spawnPanelContainer.destroy();
      this.spawnPanelContainer = null;
    }
    this.experimentUIElements.forEach(el => el.destroy());
    this.experimentUIElements = [];
    this.isExperimentMode = false;
    if (GameState.isExperimentMode()) {
      GameState.endExperimentMode();
    }

    WaveSpawner.destroy();
    TowerPlacementSystem.destroy();
    DebugSystem.destroy();
    DropSystem.shutdown();
    TowerInteractionSystem.shutdown();
    EntityManager.clear();
    PathingSystem.clearPaths();
    RenderSystem.destroy();
    SessionManager.reset();
    this.bossHealthBar?.destroy();
    this.bossHealthBar = null;
    this.wavePreviewUI?.destroy();
    this.wavePreviewUI = null;
    this.treeSlots.clear();
    this.destroyEmbers();
    this.destroyTowerSelector();
  }

  private stopMusic(): void {
    if (this.bgMusic) {
      this.bgMusic.stop();
      this.bgMusic.destroy();
      this.bgMusic = null;
    }
  }

  private toggleMusic(): void {
    this.musicMuted = !this.musicMuted;

    if (this.musicMuted) {
      if (this.bgMusic && this.bgMusic.isPlaying) {
        this.bgMusic.pause();
      }
      this.musicButtonText?.setText('🔇');
    } else {
      if (this.bgMusic) {
        this.bgMusic.resume();
      } else {
        this.playCurrentTrack();
      }
      this.musicButtonText?.setText('🔊');
    }
  }
}
